import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  startTransition,
} from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../store';
import {
  setSortConfig,
} from '../store/slices/stocksSlice';
import { showGlobalMessage } from '../store/slices/globalMessageSlice';
import { openPanel } from '../store/slices/analysisPanelSlice';
import { formatScore } from '../utils/formatScore';
import { StockData } from '../types';
import LoadingSpinner from './common/LoadingSpinner';
import EmptyState from './common/EmptyState';
import { selectSortedStocks, selectSortConfig } from '../store/selectors';
import {
  useStocksAndScreening,
  useAnalysisAndFavorites,
  useNumberFormatter,
} from '../hooks/useOptimizedSelector';
import { logStateChange } from '../utils/performanceLogger';
import { useKeyboardNavigation } from '../utils/keyboardNavigation';
import { cn } from '../lib/utils';
import {
  resolveEntryStatus,
  isKnownEntryStatus,
  resolveLiveDivergence,
  buildLiveDivergenceTooltip,
  ENTRY_STATUS_I18N_KEY,
} from '../lib/deepAnalysis/entryStatusDisplay';
import VirtualizedTable, {
  Column,
  VirtualizedTableHandle,
} from './common/VirtualizedTable';
import { screenStocksThunk, stopScreeningThunk } from '../store/thunks';
import {
  openLegacyFiltersModal,
  requestNavigateMainTab,
  requestDeepSubTab,
  setDeepAnalysisReturnTab,
} from '../store/slices/modalsSlice';
import { setSelectedTicker } from '../store/slices/analysisPanelSlice';
import { setChartTicker } from '../store/slices/chartSlice';
import { useAuth } from '../hooks/useAuth';
import StockCard from './mobile/StockCard';

interface TableColumn extends Column<StockData> {
  sortValue?: (value: any, item: StockData) => number | string;
  className?: string;
}

// --- Cup-and-Handle score rendering (score_mode === 'cup_handle') ----------
// Cup component breakdown uses 0..1 values and cup-specific keys, so it needs
// its own friendly-label tooltip + quality/status/target essentials. The
// legacy/standard score rendering (0..10 keys) is left untouched.

// Display order + friendly labels for the cup component breakdown.
const CUP_BREAKDOWN_LABELS: Array<{ key: string; label: string }> = [
  { key: 'cup_geometry', label: 'Cup Geometry' },
  { key: 'cup_handle', label: 'Handle' },
  { key: 'cup_breakout_setup', label: 'Breakout Setup' },
  { key: 'cup_volume', label: 'Volume' },
  { key: 'trend_context', label: 'Trend' },
  { key: 'target_attractiveness', label: 'Target' },
  { key: 'rsi_setup', label: 'RSI' },
  { key: 'stochastic_setup', label: 'Stochastic' },
  { key: 'ma150_proximity_bonus', label: 'MA150 Proximity' },
  { key: 'ma200_proximity_bonus', label: 'MA200 Proximity' },
];

const CUP_QUALITY_LABELS: Record<string, string> = {
  textbook: 'Textbook',
  deep_base: 'Deep base',
  early_candidate: 'Early candidate',
  low_quality: 'Low quality',
};

// A/B/C product grade chip (2026-05-24). A = clean textbook setup (green),
// B = borderline / loose candidate (amber). C is never rendered (hidden).
const CUP_GRADE_META: Record<string, { label: string; bg: string }> = {
  A: { label: 'Grade A', bg: '#16a34a' },
  B: { label: 'Grade B', bg: '#d97706' },
};

// Entry-quality grades INCLUDE C (BUG-4): unlike the cup renderer (where C is
// intentionally hidden), entry-quality C setups legitimately appear in results,
// so they get a (gray) chip rather than silently rendering no grade.
const ENTRY_QUALITY_GRADE_META: Record<string, { label: string; bg: string }> = {
  ...CUP_GRADE_META,
  C: { label: 'Grade C', bg: '#6b7280' },
};

const CUP_STATUS_LABELS: Record<string, string> = {
  candidate: 'Candidate / pre-breakout',
  confirmed_breakout: 'Confirmed breakout',
  failed_breakout: 'Failed breakout',
  invalidated: 'Invalidated',
};

// --- Darvas Box score rendering (score_mode === 'darvas_box') ---------------
// Darvas component breakdown uses 0..1 values and darvas-specific keys (the 6
// score components). Mirrors the cup renderer's friendly-label tooltip plus
// geometry essentials (box top/bottom, height/ATR, touches, containment,
// breakout proximity). Standard rendering below is left untouched.
const DARVAS_BREAKDOWN_LABELS: Array<{ key: string; label: string }> = [
  { key: 'boundary_tightness', label: 'Tightness' },
  { key: 'retest_quality', label: 'Retest' },
  { key: 'containment', label: 'Containment' },
  { key: 'duration', label: 'Duration' },
  { key: 'volume_contraction', label: 'Volume' },
  { key: 'prior_trend_strength', label: 'Prior Trend' },
];

const DARVAS_STATUS_LABELS: Record<string, string> = {
  candidate: 'Candidate / pre-breakout',
  breakout_confirmed: 'Breakout confirmed',
  failed_breakout: 'Failed breakout',
  invalidated: 'Invalidated',
};

// Darvas grades INCLUDE C from day one (BUG-4 lesson): unlike the cup renderer
// where C is intentionally hidden, all three grades legitimately render for
// Darvas results, so C gets a (gray) chip rather than rendering no grade.
const DARVAS_GRADE_META: Record<string, { label: string; bg: string }> = {
  A: { label: 'Grade A', bg: '#16a34a' },
  B: { label: 'Grade B', bg: '#d97706' },
  C: { label: 'Grade C', bg: '#6b7280' },
};

const prettyCupValue = (raw: unknown): string => {
  // Friendly fallback for unknown enum values: snake_case -> Title Case.
  if (typeof raw !== 'string' || raw.length === 0) return '';
  return raw
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
};

const renderCupHandleScore = (
  stock: StockData,
  numScore: number,
  scoreClass: string
): React.ReactElement => {
  const breakdown = stock.score_breakdown;
  const qualityLabel = stock.cah_pattern_quality
    ? CUP_QUALITY_LABELS[stock.cah_pattern_quality] ||
      prettyCupValue(stock.cah_pattern_quality)
    : '';
  const gradeMeta = stock.cah_grade ? CUP_GRADE_META[stock.cah_grade] : undefined;
  const statusLabel = stock.cah_status
    ? CUP_STATUS_LABELS[stock.cah_status] || prettyCupValue(stock.cah_status)
    : '';

  // Target essentials: price + (signed) percentage.
  let targetLabel = '';
  if (typeof stock.cah_target_price === 'number') {
    const pct =
      typeof stock.cah_target_pct === 'number'
        ? ` (${stock.cah_target_pct >= 0 ? '+' : ''}${stock.cah_target_pct.toFixed(1)}%)`
        : '';
    targetLabel = `$${stock.cah_target_price.toFixed(2)}${pct}`;
  }

  // Build a friendly multi-line tooltip mirroring the legacy title attribute.
  const lines: string[] = ['Cup & Handle score'];
  if (gradeMeta) lines.push(`Grade: ${gradeMeta.label}`);
  if (qualityLabel) lines.push(`Quality: ${qualityLabel}`);
  if (statusLabel) lines.push(`Status: ${statusLabel}`);
  if (typeof stock.cah_confidence === 'number') {
    lines.push(`Confidence: ${stock.cah_confidence.toFixed(0)}%`);
  }
  if (targetLabel) lines.push(`Target: ${targetLabel}`);
  if (typeof stock.cah_pivot_price === 'number') {
    lines.push(`Pivot: $${stock.cah_pivot_price.toFixed(2)}`);
  }
  if (typeof stock.cah_stop_loss_price === 'number') {
    lines.push(`Stop: $${stock.cah_stop_loss_price.toFixed(2)}`);
  }

  if (breakdown && typeof breakdown === 'object') {
    const bk = breakdown as Record<string, unknown>;
    const componentLines = CUP_BREAKDOWN_LABELS.filter(
      ({ key }) => typeof bk[key] === 'number'
    ).map(({ key, label }) => `${label}: ${(Number(bk[key]) * 100).toFixed(0)}%`);
    if (componentLines.length > 0) {
      lines.push('', componentLines.join(', '));
    }
  }

  const tooltipText = lines.join('\n');

  return (
    <span
      className="tv-flex tv-flex-col cursor-help"
      title={tooltipText}
      style={{ gap: '1px', lineHeight: 1.1 }}
    >
      <span className={`tv-font-bold ${scoreClass}`}>
        {formatScore(numScore)}
      </span>
      {gradeMeta && (
        <span
          className="tv-badge tv-text-xs tv-font-semibold"
          style={{
            backgroundColor: gradeMeta.bg,
            color: 'white',
            padding: '1px 4px',
            borderRadius: '8px',
            fontSize: '0.55rem',
            alignSelf: 'flex-start',
          }}
        >
          {gradeMeta.label}
        </span>
      )}
      {statusLabel && (
        <span className="tv-text-secondary" style={{ fontSize: '0.55rem' }}>
          {statusLabel}
        </span>
      )}
      {targetLabel && (
        <span className="tv-text-bull" style={{ fontSize: '0.55rem' }}>
          🎯 {targetLabel}
        </span>
      )}
    </span>
  );
};

// --- Entry Quality score rendering (score_mode === 'entry_quality') --------
const ENTRY_QUALITY_BREAKDOWN_LABELS: Array<{ key: string; label: string }> = [
  { key: 'pattern_strength', label: 'Pattern' },
  { key: 'confluence', label: 'Confluence' },
  { key: 'entry_readiness', label: 'Readiness' },
  { key: 'risk_reward', label: 'R:R' },
  { key: 'momentum_context', label: 'Momentum' },
];

const renderEntryQualityScore = (
  stock: StockData,
  numScore: number,
  scoreClass: string
): React.ReactElement => {
  const gradeMeta = stock.entry_quality_grade
    ? ENTRY_QUALITY_GRADE_META[stock.entry_quality_grade]
    : undefined;
  const setup = stock.entry_quality_setup_type || '';
  const setupHighlight = setup === 'cup+fib';
  const bd = stock.entry_quality_breakdown as Record<string, unknown> | undefined;

  const lines: string[] = ['Entry Quality score'];
  if (gradeMeta) lines.push(`Grade: ${gradeMeta.label}`);
  if (setup) lines.push(`Setup: ${setup}`);
  if (typeof stock.entry_quality_score === 'number') {
    lines.push(`Score: ${stock.entry_quality_score.toFixed(0)}/100`);
  }
  if (typeof stock.entry_quality_r_to_r === 'number') {
    lines.push(`R:R: ${stock.entry_quality_r_to_r.toFixed(2)}`);
  }
  if (typeof stock.entry_quality_target_price === 'number' && stock.entry_quality_target_price) {
    // IMP-3: currency-neutral (no hardcoded '$') - the app lists non-USD (TASE) tickers too.
    lines.push(`Target: ${stock.entry_quality_target_price.toFixed(2)}`);
  }
  if (bd) {
    const comp = ENTRY_QUALITY_BREAKDOWN_LABELS.filter(
      ({ key }) => typeof bd[key] === 'number'
    ).map(({ key, label }) => `${label}: ${(Number(bd[key]) * 100).toFixed(0)}%`);
    if (comp.length > 0) lines.push('', comp.join(', '));
  }
  const tooltipText = lines.join('\n');

  return (
    <span
      className="tv-flex tv-flex-col cursor-help"
      title={tooltipText}
      style={{ gap: '1px', lineHeight: 1.1 }}
    >
      <span className={`tv-font-bold ${scoreClass}`}>{formatScore(numScore)}</span>
      {gradeMeta && (
        <span
          className="tv-badge tv-text-xs tv-font-semibold"
          style={{
            backgroundColor: gradeMeta.bg,
            color: 'white',
            padding: '1px 4px',
            borderRadius: '8px',
            fontSize: '0.55rem',
            alignSelf: 'flex-start',
          }}
        >
          {gradeMeta.label}
        </span>
      )}
      {setup && setup !== 'none' && (
        <span
          className={setupHighlight ? 'tv-text-bull' : 'tv-text-secondary'}
          style={{ fontSize: '0.55rem', fontWeight: setupHighlight ? 700 : 400 }}
        >
          {setupHighlight ? '★ ' : ''}{setup}
        </span>
      )}
    </span>
  );
};

const renderDarvasScore = (
  stock: StockData,
  numScore: number,
  scoreClass: string
): React.ReactElement => {
  // Prefer the nested darvas_box_result payload (attached by the backend
  // /results formatter); fall back to flat row fields surfaced by the screener.
  const s = stock as Record<string, any>;
  const r = (s.darvas_box_result || {}) as Record<string, any>;
  const num = (a: unknown, b: unknown): number | undefined => {
    if (typeof a === 'number' && !Number.isNaN(a)) return a;
    if (typeof b === 'number' && !Number.isNaN(b)) return b;
    return undefined;
  };

  const grade = (r.grade ?? s.darvas_box_grade) as string | undefined;
  const gradeMeta = grade ? DARVAS_GRADE_META[grade] : undefined;
  const statusRaw = (r.status ?? s.darvas_box_status) as string | undefined;
  const statusLabel = statusRaw
    ? DARVAS_STATUS_LABELS[statusRaw] || prettyCupValue(statusRaw)
    : '';

  const boxTop = num(r.box_top, s.darvas_box_top);
  const boxBottom = num(r.box_bottom, s.darvas_box_bottom);
  const heightAtr = num(r.box_height_atr_ratio, s.darvas_box_height_atr_ratio);
  const topTouches = num(r.top_touches, s.darvas_top_touches);
  const bottomTouches = num(r.bottom_touches, s.darvas_bottom_touches);
  const containment = num(r.containment_pct, s.darvas_containment_pct);
  const proximity = num(r.breakout_proximity_pct, s.darvas_breakout_proximity_pct);
  const nearTop = Boolean(r.near_top_boundary ?? s.darvas_near_top_boundary);
  const targetPrice = num(r.target_price, s.darvas_target_price);
  const strength = num(r.strength, s.darvas_box_strength);

  // Breakout-proximity descriptor: "X% to breakout" or "near top boundary".
  let proximityLabel = '';
  if (nearTop) {
    proximityLabel = 'near top boundary';
  } else if (typeof proximity === 'number') {
    // proximity = (box_top - current)/current*100 → positive = still under ceiling.
    proximityLabel =
      proximity > 0
        ? `${proximity.toFixed(1)}% to breakout`
        : `${Math.abs(proximity).toFixed(1)}% above box top`;
  }

  // Build a friendly multi-line tooltip mirroring the cup renderer.
  const lines: string[] = ['Darvas Box score'];
  if (gradeMeta) lines.push(`Grade: ${gradeMeta.label}`);
  if (statusLabel) lines.push(`Status: ${statusLabel}`);
  if (typeof strength === 'number') {
    lines.push(`Strength: ${strength.toFixed(0)}%`);
  }
  if (typeof boxTop === 'number' && typeof boxBottom === 'number') {
    // Currency-neutral (non-USD tickers exist) - no hardcoded '$'.
    lines.push(`Box: ${boxBottom.toFixed(2)} – ${boxTop.toFixed(2)}`);
  }
  if (typeof heightAtr === 'number') {
    lines.push(`Height/ATR: ${heightAtr.toFixed(2)}`);
  }
  if (typeof topTouches === 'number' && typeof bottomTouches === 'number') {
    lines.push(`Touches: ${topTouches} top / ${bottomTouches} bottom`);
  }
  if (typeof containment === 'number') {
    // containment stored as 0..1 fraction → show as percent.
    lines.push(`Containment: ${(containment * 100).toFixed(0)}%`);
  }
  if (proximityLabel) lines.push(`Breakout: ${proximityLabel}`);
  if (typeof targetPrice === 'number') {
    lines.push(`Target: ${targetPrice.toFixed(2)}`);
  }

  const breakdown = stock.score_breakdown;
  if (breakdown && typeof breakdown === 'object') {
    const bk = breakdown as Record<string, unknown>;
    const componentLines = DARVAS_BREAKDOWN_LABELS.filter(
      ({ key }) => typeof bk[key] === 'number'
    ).map(({ key, label }) => `${label}: ${(Number(bk[key]) * 100).toFixed(0)}%`);
    if (componentLines.length > 0) {
      lines.push('', componentLines.join(', '));
    }
  }

  const tooltipText = lines.join('\n');

  return (
    <span
      className="tv-flex tv-flex-col cursor-help"
      title={tooltipText}
      style={{ gap: '1px', lineHeight: 1.1 }}
    >
      <span className={`tv-font-bold ${scoreClass}`}>
        {formatScore(numScore)}
      </span>
      {gradeMeta && (
        <span
          className="tv-badge tv-text-xs tv-font-semibold"
          style={{
            backgroundColor: gradeMeta.bg,
            color: 'white',
            padding: '1px 4px',
            borderRadius: '8px',
            fontSize: '0.55rem',
            alignSelf: 'flex-start',
          }}
        >
          {gradeMeta.label}
        </span>
      )}
      {statusLabel && (
        <span className="tv-text-secondary" style={{ fontSize: '0.55rem' }}>
          {statusLabel}
        </span>
      )}
      {proximityLabel && (
        <span
          className={nearTop ? 'tv-text-bull' : 'tv-text-secondary'}
          style={{ fontSize: '0.55rem', fontWeight: nearTop ? 700 : 400 }}
        >
          {nearTop ? '★ ' : ''}
          {proximityLabel}
        </span>
      )}
    </span>
  );
};

const ResultsTableVirtualized: React.FC = React.memo(() => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isAdmin } = useAuth();

  // Use optimized selectors to prevent cascade re-renders
  const { loading, screeningStatus, screeningResults } =
    useStocksAndScreening();

  // Use memoized sorted stocks from selector
  const allStocks = useAppSelector(selectSortedStocks);
  const sortConfig = useAppSelector(selectSortConfig);
  const { analysisPanel } = useAnalysisAndFavorites();
  const { formatNumber, formatMarketCap } = useNumberFormatter();

  // Get RSI Recovery filter status from Redux
  const filters = useAppSelector((state) => state.filters);
  const rsiRecoveryEnabled = filters.rsi_recovery_enabled || false;

  const lastClickedTicker = useAppSelector(
    (state) => state.stocks.lastClickedTicker
  );
  const tableRef = useRef<VirtualizedTableHandle>(null);

  const [selectedStocks, setSelectedStocks] = useState<Set<string>>(() => {
    if (analysisPanel.isOpen && analysisPanel.selectedTicker) {
      return new Set([analysisPanel.selectedTicker]);
    }
    return new Set();
  });
  const [currentRowIndex, setCurrentRowIndex] = useState<number>(-1);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number>(-1);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFeedback, setExportFeedback] = useState<
    'all' | 'selected' | 'csv' | null
  >(null);
  const exportFeedbackTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashExportFeedback = useCallback(
    (which: 'all' | 'selected' | 'csv') => {
      if (exportFeedbackTimerRef.current) {
        clearTimeout(exportFeedbackTimerRef.current);
      }
      setExportFeedback(which);
      exportFeedbackTimerRef.current = setTimeout(() => {
        setExportFeedback(null);
        exportFeedbackTimerRef.current = null;
      }, 1500);
    },
    []
  );
  React.useEffect(
    () => () => {
      if (exportFeedbackTimerRef.current) {
        clearTimeout(exportFeedbackTimerRef.current);
      }
    },
    []
  );
  const [buySignalsLoading] = useState(false);
  const [buySignalsEnabled] = useState(true);
  const [showOnlySupportLines, setShowOnlySupportLines] = useState(false);
  // Cup&Handle grading: hide borderline Grade-B rows by default; A is always
  // shown, C never reaches the client (detected=False). Toggle reveals B inline.
  const [showBorderline, setShowBorderline] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Combinable result filters: numeric Score range (string '' = unset bound)
  // + multi-select Entry-Status (array of status codes, [] = all).
  const [scoreMin, setScoreMin] = useState('');
  const [scoreMax, setScoreMax] = useState('');
  const [entryStatusFilter, setEntryStatusFilter] = useState<string[]>([]);
  const [entryStatusMenuOpen, setEntryStatusMenuOpen] = useState(false);
  const entryStatusMenuRef = React.useRef<HTMLDivElement>(null);
  const [toolbarMenuOpen, setToolbarMenuOpen] = useState(false);
  const toolbarMenuRef = React.useRef<HTMLDivElement>(null);
  const [sheetDragY, setSheetDragY] = useState(0);
  const sheetDragStartYRef = useRef<number | null>(null);
  const [sheetDragEnabled, setSheetDragEnabled] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(400);
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  const isTestEnv =
    process.env.NODE_ENV === 'test' ||
    typeof (globalThis as any).jest !== 'undefined';

  // Restore scroll position to last-clicked stock when the component remounts.
  // Derives row index from ticker so it's stable across filter/sort changes.
  // Retries when allStocks loads or when switching from mobile to desktop view.
  const hasRestoredScroll = useRef(false);
  useEffect(() => {
    if (hasRestoredScroll.current || !lastClickedTicker || !tableRef.current)
      return;
    const index = allStocks.findIndex(
      (s: StockData) => s.ticker === lastClickedTicker
    );
    if (index >= 0) {
      hasRestoredScroll.current = true;
      setCurrentRowIndex(index);
      requestAnimationFrame(() => {
        tableRef.current?.scrollToItem(index, 'smart');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStocks.length, isMobileView]);

  const ROW_HEIGHT = 80;
  const MIN_LIST_HEIGHT = 200;
  // When the list has at most this many rows, render it at its natural full
  // height (no internal scroll). Above this threshold we keep
  // virtualization-friendly internal scrolling.
  const NATURAL_HEIGHT_ROW_CAP = 12;
  // Read inside the height-calc callback. Updated after the `stocks` memo
  // below so the callback can be defined before it without a TDZ error.
  const visibleRowCountRef = useRef(0);

  // Bootstrap height before ResizeObserver fires. Underestimates chrome
  // (topnav + action buttons + filter pills + results header + toolbar +
  // column header + footer) so the list isn't tiny on first paint.
  const calculateTableHeight = useCallback(() => {
    const viewportHeight = window.innerHeight;
    const estimatedChromeHeight = 300;
    return Math.max(viewportHeight - estimatedChromeHeight, MIN_LIST_HEIGHT);
  }, []);

  // Compute the list viewport height so all rows are visible when they fit
  // in the viewport, and the list scrolls inside otherwise.
  //
  // Parents on desktop screening now use overflow-visible (App.tsx + tv-card)
  // and Main Content Container has md:overflow-y-auto, so a list taller than
  // the flex container will extend the page and the page itself scrolls
  // instead of the list being clipped.
  const updateTableHeight = useCallback(
    (containerHeight?: number) => {
      const rowCount = visibleRowCountRef.current;
      const totalRowsHeight = rowCount * ROW_HEIGHT;
      const containerH =
        typeof containerHeight === 'number' && containerHeight > 0
          ? containerHeight
          : calculateTableHeight();

      // Small lists: render all rows at their natural height. The list
      // extends past the container's flex-1 share, parents are
      // overflow-visible on desktop, and the page scrolls so every row is
      // reachable on short laptop viewports.
      // Large lists: keep virtualized internal scrolling at the container
      // height to avoid mounting thousands of row DOM nodes.
      const target =
        rowCount > 0 && rowCount <= NATURAL_HEIGHT_ROW_CAP
          ? totalRowsHeight
          : containerH;

      setTableHeight(Math.max(target, MIN_LIST_HEIGHT));
    },
    [calculateTableHeight]
  );

  useEffect(() => {
    // Set initial height
    updateTableHeight();

    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
      updateTableHeight();
    };
    window.addEventListener('resize', handleResize);

    const el = tableContainerRef.current;
    let ro: ResizeObserver | null = null;
    if (el) {
      ro = new ResizeObserver((entries) => {
        const h = entries[0]?.contentRect.height;
        if (typeof h === 'number' && h > 0) {
          updateTableHeight(h);
        }
      });
      ro.observe(el);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      ro?.disconnect();
    };
  }, [updateTableHeight]);

  // Close toolbar overflow menu on click outside (desktop popover fallback)
  useEffect(() => {
    if (!toolbarMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        toolbarMenuRef.current &&
        !toolbarMenuRef.current.contains(e.target as Node)
      ) {
        setToolbarMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [toolbarMenuOpen]);

  // Close the Entry-Status multi-select popover on outside click / Escape.
  useEffect(() => {
    if (!entryStatusMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        entryStatusMenuRef.current &&
        !entryStatusMenuRef.current.contains(e.target as Node)
      ) {
        setEntryStatusMenuOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEntryStatusMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [entryStatusMenuOpen]);

  // Native-like modal behavior for mobile bottom sheet
  useEffect(() => {
    if (!isMobileView || !toolbarMenuOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setToolbarMenuOpen(false);
    };
    document.addEventListener('keydown', onEsc);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onEsc);
    };
  }, [isMobileView, toolbarMenuOpen]);

  useEffect(() => {
    if (!toolbarMenuOpen) setSheetDragY(0);
  }, [toolbarMenuOpen]);

  const startHandleDrag = useCallback((clientY: number) => {
    sheetDragStartYRef.current = clientY;
    setSheetDragEnabled(true);
  }, []);

  const endHandleDrag = useCallback(() => {
    sheetDragStartYRef.current = null;
    setSheetDragEnabled(false);
  }, []);

  const handleSheetTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!sheetDragEnabled) return;
      sheetDragStartYRef.current = e.touches[0]?.clientY ?? null;
    },
    [sheetDragEnabled]
  );

  const handleSheetTouchMove = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!sheetDragEnabled || sheetDragStartYRef.current === null) return;
      const currentY = e.touches[0]?.clientY ?? sheetDragStartYRef.current;
      const delta = currentY - sheetDragStartYRef.current;
      if (delta > 0) {
        setSheetDragY(Math.min(delta, 220));
      }
    },
    [sheetDragEnabled]
  );

  const handleSheetTouchEnd = useCallback(() => {
    if (!sheetDragEnabled) return;
    const closeThreshold = 90;
    if (sheetDragY >= closeThreshold) {
      setToolbarMenuOpen(false);
    } else {
      setSheetDragY(0);
    }
    endHandleDrag();
  }, [sheetDragEnabled, sheetDragY, endHandleDrag]);

  // A row only passes the Support Filter when it has BOTH detection AND the
  // geometry the filter keys on (`support_line_coords`). Named so the filter
  // predicate below and the visibility/disable gate (`anySupportLineFilterable`)
  // cannot drift apart - gating on `detected` alone would still leave an empty
  // table for rows detected without coords.
  const hasFilterableSupportLine = useCallback(
    (s: StockData) =>
      Boolean(
        s.strong_support_result?.detected &&
          s.strong_support_result?.support_line_coords
      ),
    []
  );

  // Apply support line filter and search filter
  const stocks = useMemo(() => {
    let filtered = allStocks;

    // Apply support line filter
    if (showOnlySupportLines) {
      filtered = filtered.filter(hasFilterableSupportLine);
    }

    // Hide borderline Grade-B cups unless opted in. Non-cup rows have no
    // cah_grade and are unaffected; Grade C never reaches the client.
    if (!showBorderline) {
      filtered = filtered.filter((stock: StockData) => stock.cah_grade !== 'B');
    }

    // Apply search filter (prioritize matches from beginning of words)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();

      // Filter stocks
      filtered = filtered.filter((stock: StockData) => {
        const tickerLower = stock.ticker.toLowerCase();
        const companyLower = stock.company_name?.toLowerCase() || '';

        // Check if ticker starts with search term (highest priority)
        if (tickerLower.startsWith(searchLower)) {
          return true;
        }

        // Check if any word in company name starts with search term
        if (companyLower) {
          const words = companyLower.split(/\s+/); // Split by whitespace
          if (words.some((word: string) => word.startsWith(searchLower))) {
            return true;
          }
        }

        // Fallback: partial match anywhere in ticker or company name
        return (
          tickerLower.includes(searchLower) ||
          companyLower.includes(searchLower)
        );
      });

      // Sort results by match quality (best matches first)
      filtered = filtered.sort((a: StockData, b: StockData) => {
        const aTickerLower = a.ticker.toLowerCase();
        const bTickerLower = b.ticker.toLowerCase();
        const aCompanyLower = a.company_name?.toLowerCase() || '';
        const bCompanyLower = b.company_name?.toLowerCase() || '';

        // Priority 1: Exact ticker match
        const aExactTicker = aTickerLower === searchLower;
        const bExactTicker = bTickerLower === searchLower;
        if (aExactTicker && !bExactTicker) return -1;
        if (!aExactTicker && bExactTicker) return 1;

        // Priority 2: Ticker starts with search
        const aTickerStarts = aTickerLower.startsWith(searchLower);
        const bTickerStarts = bTickerLower.startsWith(searchLower);
        if (aTickerStarts && !bTickerStarts) return -1;
        if (!aTickerStarts && bTickerStarts) return 1;

        // Priority 3: Company name word starts with search
        const aCompanyWordStarts = aCompanyLower
          .split(/\s+/)
          .some((word: string) => word.startsWith(searchLower));
        const bCompanyWordStarts = bCompanyLower
          .split(/\s+/)
          .some((word: string) => word.startsWith(searchLower));
        if (aCompanyWordStarts && !bCompanyWordStarts) return -1;
        if (!aCompanyWordStarts && bCompanyWordStarts) return 1;

        // Keep original order for similar match quality
        return 0;
      });
    }

    // Score numeric-range filter (combinable). Each bound is optional; an
    // unset ('' or non-finite) bound is ignored. A NaN/missing score is only
    // dropped when at least one bound is active so the column stays inclusive
    // when no range is requested.
    let minBound = scoreMin.trim() === '' ? null : Number(scoreMin);
    let maxBound = scoreMax.trim() === '' ? null : Number(scoreMax);
    let hasMin = minBound !== null && Number.isFinite(minBound);
    let hasMax = maxBound !== null && Number.isFinite(maxBound);
    // Order-independent range: "from 9 till 5" means scores in [5, 9]. When both
    // bounds are set, treat the smaller as the floor and the larger as the ceil.
    if (hasMin && hasMax && (minBound as number) > (maxBound as number)) {
      const lo = maxBound as number;
      maxBound = minBound;
      minBound = lo;
    }
    if (hasMin || hasMax) {
      filtered = filtered.filter((stock: StockData) => {
        const numScore = Number(stock.score);
        if (Number.isNaN(numScore)) return false;
        if (hasMin && numScore < (minBound as number)) return false;
        if (hasMax && numScore > (maxBound as number)) return false;
        return true;
      });
    }

    // Entry-status multi-select filter (combinable). Empty selection = all.
    // Rows with a missing entry_status are treated as 'unknown'; the deprecated
    // `in_buy_zone` alias folds into `accumulate` so those rows match the
    // Accumulate checkbox instead of being orphaned.
    if (entryStatusFilter.length) {
      const allowed = new Set(entryStatusFilter);
      filtered = filtered.filter((stock: StockData) => {
        const raw = stock.entry_status ?? 'unknown';
        const code = raw === 'in_buy_zone' ? 'accumulate' : raw;
        return allowed.has(code);
      });
    }

    return filtered;
  }, [
    allStocks,
    showOnlySupportLines,
    hasFilterableSupportLine,
    searchTerm,
    showBorderline,
    scoreMin,
    scoreMax,
    entryStatusFilter,
  ]);

  // Count of borderline Grade-B cup rows available to reveal (drives the toggle's
  // visibility + label). Computed off the unfiltered set so the count is stable.
  const borderlineCount = useMemo(
    () => allStocks.filter((s: StockData) => s.cah_grade === 'B').length,
    [allStocks]
  );

  const anySupportLineFilterable = useMemo(
    () => allStocks.some(hasFilterableSupportLine),
    [allStocks, hasFilterableSupportLine]
  );

  // Clear a stale Support Filter: if it is on but the current result set has no
  // filterable row, drop it so the toggle can't leave an empty table.
  useEffect(() => {
    if (showOnlySupportLines && !anySupportLineFilterable) {
      setShowOnlySupportLines(false);
    }
  }, [showOnlySupportLines, anySupportLineFilterable]);

  // Selected tickers that are within the CURRENT (filtered) view - i.e. what
  // "Export Selected" will actually copy. The selection Set persists across
  // filters, so its raw size can exceed the visible rows; showing that raw size
  // made "Export Selected (267)" read larger than "Export All (33)" once an
  // Entry-Status filter narrowed the table. Counting the intersection keeps
  // Selected ≤ All and matches the rows the export already filters to (≈line 2402).
  const selectedVisibleCount = useMemo(
    () => stocks.reduce((n: number, s: StockData) => (selectedStocks.has(s.ticker) ? n + 1 : n), 0),
    [stocks, selectedStocks]
  );

  // Selectable Entry-Status codes for the multi-select filter (display order
  // mirrors the conviction rank, best-first). `unknown` is offered last so a
  // user can isolate rows with no resolved status. Codes are the SSOT from
  // entryStatusDisplay.ts; deprecated `in_buy_zone` is folded into accumulate
  // for display and intentionally not a separate checkbox.
  const ENTRY_STATUS_FILTER_CODES = useMemo(
    () => [
      'strong_buy',
      'buy',
      'accumulate',
      'wait_for_reversal',
      'holding',
      'extended',
      'below_stop',
      'unknown',
    ] as const,
    []
  );

  const toggleEntryStatus = useCallback((code: string) => {
    setEntryStatusFilter((prev) =>
      prev.includes(code)
        ? prev.filter((c) => c !== code)
        : [...prev, code]
    );
  }, []);

  const scoreFilterActive = scoreMin.trim() !== '' || scoreMax.trim() !== '';
  const anyResultFilterActive =
    scoreFilterActive || entryStatusFilter.length > 0;

  const clearResultFilters = useCallback(() => {
    setScoreMin('');
    setScoreMax('');
    setEntryStatusFilter([]);
  }, []);

  // Keep the row-count ref in sync so updateTableHeight (defined earlier,
  // before `stocks`) can read the latest count without a TDZ violation.
  // Recompute table height whenever the visible row count changes so the
  // list grows/shrinks to match.
  useEffect(() => {
    visibleRowCountRef.current = stocks.length;
    updateTableHeight();
  }, [stocks.length, updateTableHeight]);

  // Optimized debug logging for state changes
  useEffect(() => {
    logStateChange('ResultsTable', {
      stocksCount: stocks.length,
      screeningStatus: screeningStatus.status,
      screeningResultsLength: screeningResults?.length || 0,
    });
  }, [stocks.length, screeningStatus.status, screeningResults?.length]);

  // Remove store updates from view component - these are now handled by thunks

  // Sync selected row with analysis panel
  useEffect(() => {
    if (analysisPanel.isOpen && analysisPanel.selectedTicker) {
      setSelectedStocks(new Set([analysisPanel.selectedTicker]));
      // Find and set current row index
      const rowIndex = stocks.findIndex(
        (stock: StockData) => stock.ticker === analysisPanel.selectedTicker
      );
      if (rowIndex !== -1) {
        setCurrentRowIndex(rowIndex);
      }
    }
  }, [analysisPanel.isOpen, analysisPanel.selectedTicker, stocks]);

  // Optimized keyboard navigation
  useKeyboardNavigation(
    currentRowIndex,
    stocks.length,
    setCurrentRowIndex,
    (newIndex: number) => {
      const selectedStock = stocks[newIndex];
      if (selectedStock) {
        const selectedTicker = selectedStock.ticker;
        setSelectedStocks(new Set([selectedTicker]));
        dispatch(openPanel({ ticker: selectedTicker }));
      }
    },
    (index: number) => `tr[data-ticker="${stocks[index]?.ticker}"]`,
    { enabled: stocks.length > 0 }
  );

  // Render functions for complex cells
  const renderEntryRationale = useCallback((stock: StockData) => {
    if (!stock.entry_rationale) return null;

    if (stock.entry_rationale.includes('SECOND BOTTOM ENTRY POINT')) {
      return (
        <span
          className="tv-badge tv-text-xs tv-font-semibold"
          style={{
            backgroundColor: '#28a745',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '12px',
            fontSize: '0.625rem',
          }}
          title={t('resultsTableVirtualized.secondBottomTitle', { defaultValue: '🎯 At Second Bottom - Prime Entry Opportunity' })}
        >
          🎯 {t('resultsTableVirtualized.secondBottom', { defaultValue: 'SECOND BOTTOM' })}
        </span>
      );
    } else if (stock.entry_rationale.includes('NECKLINE BREAKOUT CONFIRMED')) {
      return (
        <span
          className="tv-badge tv-text-xs tv-font-semibold"
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '12px',
            fontSize: '0.625rem',
          }}
          title={t('resultsTableVirtualized.breakoutTitle', { defaultValue: '🚀 Breakout Confirmed - Momentum Opportunity' })}
        >
          🚀 {t('resultsTableVirtualized.breakout', { defaultValue: 'BREAKOUT' })}
        </span>
      );
    } else if (stock.entry_rationale.includes('DOUBLE BOTTOM')) {
      return (
        <span
          className="tv-badge tv-text-xs"
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '12px',
            fontSize: '0.625rem',
          }}
          title={t('resultsTableVirtualized.doubleBottomTitle', { defaultValue: '🔄 Double Bottom Pattern Detected' })}
        >
          🔄 {t('resultsTableVirtualized.doubleBottom', { defaultValue: 'DOUBLE BOTTOM' })}
        </span>
      );
    }

    return (
      <span
        className="tv-text-xs"
        style={{
          color: 'var(--tv-text-secondary)',
          fontSize: '0.6875rem',
          maxWidth: '180px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={stock.entry_rationale}
      >
        {stock.entry_rationale}
      </span>
    );
  }, [t]);

  // renderStockType function removed - was unused (column is commented out)

  // Entry Status (buy-readiness) badge - code → {label, tone, emoji} per
  // docs/SPEC_ENTRY_STATUS_2026-06-18.md §3.
  const renderEntryStatus = useCallback((stock: StockData) => {
    // Single source of truth for label/emoji/tone: entryStatusDisplay.ts
    // (shared with the deep-chart pill + mobile StockCard). i18n stays here via
    // t() with the SSOT label as defaultValue.
    const code = stock.entry_status;
    if (!isKnownEntryStatus(code)) {
      return (
        <span
          className="tv-text-xs tv-text-secondary"
          title={t('resultsTableVirtualized.entryStatusUnknown', {
            defaultValue: 'Entry status unavailable',
          })}
        >
          -
        </span>
      );
    }

    const display = resolveEntryStatus(code);
    const label = t(`resultsTableVirtualized.${ENTRY_STATUS_I18N_KEY[display.code]}`, {
      defaultValue: display.label,
    });
    // Live-divergence hint (PLAN_ENTRY_STATUS_PARITY_2026-06-22) - shared decision
    // via resolveLiveDivergence (same helper EntryStatusBadge uses, so the two
    // surfaces can't drift). `entry_status` is the SCREENED grade; live_entry_status
    // is the coarse LIVE membership. They differ ONLY when a graded buy code was
    // kept through an intraday drift (screened Buy, live Holding).
    const { show: showLiveHint, liveDisplay } = resolveLiveDivergence(code, stock.live_entry_status);
    const liveLabel = liveDisplay
      ? t(`resultsTableVirtualized.${ENTRY_STATUS_I18N_KEY[liveDisplay.code]}`, {
          defaultValue: liveDisplay.label,
        })
      : '';
    // Prefer the backend's plain-language entry_instruction as the hover tooltip
    // (SPEC_ENTRY_CONVICTION_2026-06-19 - "buy now" vs "wait for a reversal
    // candle"); fall back to the badge label when no instruction was shipped.
    const baseTooltip =
      typeof stock.entry_instruction === 'string' && stock.entry_instruction.trim()
        ? stock.entry_instruction
        : label;
    const tooltip = showLiveHint
      ? buildLiveDivergenceTooltip(
          baseTooltip,
          t('resultsTableVirtualized.liveStatusPrefix', { defaultValue: 'Live' }),
          liveLabel,
        )
      : baseTooltip;

    return (
      <span
        className={`tv-badge tv-text-xs tv-font-semibold ${display.textClass}`}
        style={{
          padding: '2px 6px',
          borderRadius: '12px',
          fontSize: '0.625rem',
          backgroundColor: 'var(--tv-bg-secondary)',
          whiteSpace: 'nowrap',
        }}
        title={tooltip}
      >
        {display.emoji} {label}
        {showLiveHint && liveDisplay && (
          <span aria-hidden style={{ opacity: 0.6, marginLeft: 3 }}>{liveDisplay.emoji || '•'}</span>
        )}
      </span>
    );
  }, [t]);

  const renderGapDownResult = useCallback((stock: StockData) => {
    if (!stock.gap_down_result || !stock.gap_down_result.has_gap) {
      return (
        <div className="tv-text-center tv-text-muted tv-text-sm">
          <i className="fas fa-minus"></i>
          <div className="tv-text-xs">No Gap</div>
        </div>
      );
    }

    const { gap_down_result } = stock;
    return (
      <div className="tv-flex tv-flex-col tv-items-center tv-gap-1">
        <span
          className="tv-badge tv-text-xs tv-font-semibold"
          style={{
            backgroundColor: gap_down_result.is_unfilled
              ? '#dc3545'
              : '#6c757d',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '12px',
            fontSize: '0.625rem',
          }}
          title={`Gap Down: ${gap_down_result.gap_percent?.toFixed(1)}%${
            gap_down_result.gap_date
              ? ` on ${new Date(gap_down_result.gap_date).toLocaleDateString()}`
              : ''
          }${gap_down_result.is_true_gap ? ' (True Gap)' : ''}${
            gap_down_result.is_unfilled ? ' - Unfilled' : ' - Filled'
          }`}
        >
          📉 {gap_down_result.gap_percent?.toFixed(1)}%
        </span>
        <div className="tv-text-xs tv-text-center">
          <div className="tv-flex tv-items-center tv-justify-center tv-gap-1">
            {gap_down_result.is_true_gap && (
              <span
                className="tv-badge tv-text-xs"
                style={{
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  padding: '1px 4px',
                  borderRadius: '8px',
                  fontSize: '0.5rem',
                }}
                title="True gap with no intraday overlap"
              >
                TRUE
              </span>
            )}
            {gap_down_result.is_unfilled !== undefined && (
              <span
                className="tv-badge tv-text-xs"
                style={{
                  backgroundColor: gap_down_result.is_unfilled
                    ? '#ffc107'
                    : '#28a745',
                  color: gap_down_result.is_unfilled ? '#000' : 'white',
                  padding: '1px 4px',
                  borderRadius: '8px',
                  fontSize: '0.5rem',
                }}
                title={
                  gap_down_result.is_unfilled
                    ? 'Gap not yet filled'
                    : 'Gap has been filled'
                }
              >
                {gap_down_result.is_unfilled ? 'UNFILLED' : 'FILLED'}
              </span>
            )}
          </div>
          {gap_down_result.gap_date && (
            <div
              className="tv-text-xs tv-text-muted mt-1"
              style={{ fontSize: '0.625rem' }}
            >
              {new Date(gap_down_result.gap_date).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    );
  }, []);

  const getBuySignalColor = useCallback((score: number) => {
    if (score >= 7) return 'tv-text-bull'; // Green for good buy signals
    if (score >= 4) return 'tv-text-warning'; // Orange for neutral
    return 'tv-text-bear'; // Red for poor buy signals
  }, []);

  const getBuySignalBadgeColor = useCallback((score: number) => {
    if (score >= 7) return 'var(--tv-bull)'; // Green background
    if (score >= 4) return 'var(--tv-warning)'; // Orange background
    return 'var(--tv-bear)'; // Red background
  }, []);

  const renderAIBuySignal = useCallback(
    (stock: StockData) => {
      if (!stock.ai_buy_signal) {
        if (buySignalsLoading) {
          return (
            <div className="tv-text-center">
              <LoadingSpinner size="sm" />
              <div className="tv-text-xs tv-text-muted">Analyzing...</div>
            </div>
          );
        }
        return (
          <div className="tv-text-center tv-text-muted tv-text-sm">
            <i className="fas fa-robot"></i>
            <div className="tv-text-xs">Not analyzed</div>
          </div>
        );
      }

      if (!stock.ai_buy_signal.success) {
        return (
          <div className="tv-text-center">
            <div className="tv-text-danger tv-text-sm">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <div className="tv-text-xs tv-text-muted">Analysis Failed</div>
          </div>
        );
      }

      const { ai_buy_signal } = stock;
      return (
        <div className="tv-flex tv-flex-col tv-items-center tv-gap-1">
          <div
            className="tv-badge tv-font-semibold"
            style={{
              backgroundColor: getBuySignalBadgeColor(ai_buy_signal.buy_score),
              color: 'white',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.875rem',
            }}
            title={`Buy Score: ${
              ai_buy_signal.buy_score
            }/10\nPrice: $${ai_buy_signal.current_price.toFixed(
              2
            )}\nConfidence: ${ai_buy_signal.confidence}\n\n${
              ai_buy_signal.explanation
            }`}
          >
            {ai_buy_signal.buy_score}
          </div>
          <div
            className="tv-text-xs tv-text-center"
            style={{ maxWidth: '100px', lineHeight: '1.2' }}
          >
            <div className="tv-font-medium">
              ${ai_buy_signal.current_price.toFixed(2)}
            </div>
            <div
              className={`${getBuySignalColor(
                ai_buy_signal.buy_score
              )} tv-text-xs`}
            >
              {ai_buy_signal.buy_score >= 7
                ? 'BUY'
                : ai_buy_signal.buy_score >= 4
                  ? 'HOLD'
                  : 'AVOID'}
            </div>
          </div>
        </div>
      );
    },
    [buySignalsLoading, getBuySignalBadgeColor, getBuySignalColor]
  );

  const renderGoldenCrossResult = useCallback((stock: StockData) => {
    const result = stock.golden_cross_result;

    if (!result || !result.has_golden_cross) {
      return (
        <span className="tv-text-muted" style={{ fontSize: '0.75rem' }}>
          -
        </span>
      );
    }

    // Determine strength label
    const strength = result.strength_score || 0;
    const strengthLabel =
      strength > 2.0 ? 'Strong' : strength > 1.0 ? 'Moderate' : 'Weak';
    const stars = strength > 2.0 ? '🌟🌟🌟' : strength > 1.0 ? '🌟🌟' : '🌟';

    // Extract days since crossover from description
    const match = result.description?.match(/crossed (\\d+) days ago/);
    const daysAgo = match ? match[1] : '?';

    return (
      <div
        style={{ fontSize: '0.75rem', cursor: 'pointer' }}
        title={result.description || 'Golden Cross detected'}
      >
        <div>
          <span
            className="tv-badge"
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              fontSize: '0.6875rem',
              padding: '4px 8px',
              borderRadius: '6px',
              display: 'inline-block',
            }}
          >
            {stars} {strengthLabel}
          </span>
        </div>
        <small
          className="tv-text-muted"
          style={{ fontSize: '0.625rem', marginTop: '2px', display: 'block' }}
        >
          {daysAgo} days ago
        </small>
      </div>
    );
  }, []);

  const renderStrongSupportResult = useCallback((stock: StockData) => {
    const result = stock.strong_support_result;

    if (!result || !result.detected) {
      return (
        <div className="tv-text-center tv-text-muted tv-text-sm">
          <i className="fas fa-minus"></i>
          <div className="tv-text-xs">No Support</div>
        </div>
      );
    }

    // Determine badge color based on support type
    const supportTypeColor =
      result.support_type === 'ascending' ? '#28a745' : '#17a2b8';
    const supportIcon = result.support_type === 'ascending' ? '↗' : '-';

    // Determine strength color
    const strengthColor =
      result.strength >= 75
        ? '#28a745'
        : result.strength >= 60
          ? '#ffc107'
          : '#6c757d';

    // Check if support line coordinates are available for chart visualization
    const hasSupportLineCoords = !!result.support_line_coords;

    return (
      <div
        className="tv-flex tv-flex-col tv-items-center tv-gap-1"
        style={{ minWidth: '180px' }}
      >
        {/* Main Support Badge */}
        <div className="tv-flex tv-items-center tv-gap-1">
          <span
            className="tv-badge tv-text-xs tv-font-semibold"
            style={{
              backgroundColor: supportTypeColor,
              color: 'white',
              padding: '3px 8px',
              borderRadius: '12px',
              fontSize: '0.6875rem',
            }}
            title={`${
              result.support_type === 'ascending' ? 'Ascending' : 'Horizontal'
            } Support - ${
              result.touches
            } touches - Click row to view support line on chart`}
          >
            {supportIcon}{' '}
            {result.support_type === 'ascending' ? 'Ascending' : 'Horizontal'}
            {hasSupportLineCoords && (
              <i
                className="fas fa-chart-line"
                style={{ marginLeft: '4px', fontSize: '0.5625rem' }}
              ></i>
            )}
          </span>
          <span
            className="tv-badge tv-text-xs"
            style={{
              backgroundColor: strengthColor,
              color: 'white',
              padding: '3px 6px',
              borderRadius: '8px',
              fontSize: '0.625rem',
            }}
            title={`Strength Score: ${result.strength}/100`}
          >
            {result.strength.toFixed(0)}/100
          </span>
        </div>

        {/* Key Metrics */}
        <div className="tv-text-xs" style={{ lineHeight: '1.4' }}>
          <div className="tv-flex tv-justify-between tv-gap-2">
            <span className="tv-text-muted">Level:</span>
            <span className="tv-font-semibold">
              ${result.support_level.toFixed(2)}
            </span>
          </div>
          <div className="tv-flex tv-justify-between tv-gap-2">
            <span className="tv-text-muted">Entry:</span>
            <span className="tv-text-success tv-font-medium">
              ${result.entry_price.toFixed(2)}
            </span>
          </div>
          <div className="tv-flex tv-justify-between tv-gap-2">
            <span className="tv-text-muted">Stop:</span>
            <span className="tv-text-danger tv-font-medium">
              ${result.stop_loss.toFixed(2)}
            </span>
          </div>
          <div className="tv-flex tv-justify-between tv-gap-2">
            <span className="tv-text-muted">Target:</span>
            <span className="tv-text-success tv-font-semibold">
              ${result.target.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Additional Indicators */}
        <div className="tv-flex tv-flex-wrap tv-gap-1 tv-justify-center">
          <span
            className="tv-badge tv-text-xs"
            style={{
              backgroundColor: '#e0e0e0',
              color: '#333',
              padding: '2px 5px',
              borderRadius: '6px',
              fontSize: '0.5625rem',
            }}
            title={`${result.touches} support touches detected`}
          >
            {result.touches} touches
          </span>

          {result.volume_confirmed && (
            <span
              className="tv-badge tv-text-xs"
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                padding: '2px 5px',
                borderRadius: '6px',
                fontSize: '0.5625rem',
              }}
              title="Volume confirmed on support touches"
            >
              ✓ Volume
            </span>
          )}
        </div>

        {/* Reclaim & Kickoff Indicators */}
        {result.support_reclaim_detected && (
          <div
            className="tv-flex tv-flex-col tv-gap-1"
            style={{ marginTop: '2px' }}
          >
            <span
              className="tv-badge tv-text-xs tv-font-semibold"
              style={{
                backgroundColor: '#ff9800',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '8px',
                fontSize: '0.5625rem',
              }}
              title={`Bullish reclaim detected: ${
                result.support_reclaim_type || 'unknown'
              } type`}
            >
              ⚡ Reclaim: {result.support_reclaim_type || 'detected'}
            </span>

            {result.momentum_kickoff_ready && (
              <span
                className="tv-badge tv-text-xs tv-font-semibold"
                style={{
                  backgroundColor: result.kickoff_started
                    ? '#28a745'
                    : '#17a2b8',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: '8px',
                  fontSize: '0.5625rem',
                }}
                title={
                  result.kickoff_started
                    ? 'Momentum kickoff has started!'
                    : 'Ready for momentum kickoff'
                }
              >
                {result.kickoff_started
                  ? '🚀 Kickoff Started'
                  : '📊 Kickoff Ready'}
              </span>
            )}
          </div>
        )}

        {/* Reason Tooltip */}
        {result.reason && (
          <div
            className="tv-text-xs tv-text-muted tv-text-center"
            style={{
              fontSize: '0.5625rem',
              maxWidth: '180px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              marginTop: '2px',
            }}
            title={result.reason}
          >
            {result.reason.substring(0, 50)}...
          </div>
        )}
      </div>
    );
  }, []);

  const renderCurrentRSI = useCallback((stock: StockData) => {
    const currentRsi = stock.current_rsi;

    if (currentRsi === null || currentRsi === undefined) {
      return (
        <div className="tv-text-center tv-text-muted tv-text-sm">
          <span style={{ fontSize: '0.6875rem' }}>N/A</span>
        </div>
      );
    }

    // Color coding based on RSI levels
    // < 30: Oversold (red)
    // 30-50: Recovery zone (yellow/gold)
    // 50-70: Neutral (green)
    // > 70: Overbought (red)
    let bgColor = '';
    let textColor = 'white';
    let label = '';

    if (currentRsi < 30) {
      bgColor = '#dc3545'; // Red - Oversold
      label = t('resultsTableVirtualized.rsiOversold', { defaultValue: 'Oversold' });
    } else if (currentRsi >= 30 && currentRsi <= 50) {
      bgColor = '#ffc107'; // Yellow/Gold - Recovery Zone
      textColor = '#000';
      label = t('resultsTableVirtualized.rsiRecovery', { defaultValue: 'Recovery' });
    } else if (currentRsi > 50 && currentRsi <= 70) {
      bgColor = '#28a745'; // Green - Neutral/Bullish
      label = t('resultsTableVirtualized.rsiBullish', { defaultValue: 'Bullish' });
    } else {
      bgColor = '#dc3545'; // Red - Overbought
      label = t('resultsTableVirtualized.rsiOverbought', { defaultValue: 'Overbought' });
    }

    return (
      <div className="tv-flex tv-flex-col tv-items-center tv-gap-1">
        <span
          className="tv-font-mono tv-font-semibold"
          style={{
            color: bgColor,
            fontSize: '0.875rem',
            fontWeight: '700',
          }}
        >
          {currentRsi.toFixed(1)}
        </span>
        <span
          className="tv-badge tv-text-xs"
          style={{
            backgroundColor: bgColor,
            color: textColor,
            padding: '2px 6px',
            borderRadius: '8px',
            fontSize: '0.5625rem',
            fontWeight: '600',
            textTransform: 'uppercase',
          }}
          title={`RSI ${currentRsi.toFixed(1)} - ${label}`}
        >
          {label}
        </span>
      </div>
    );
  }, [t]);

  // Render combined AI Analysis column with Grok and Perplexity buttons
  const renderAIAnalysis = useCallback(
    (stock: StockData) => {
      return (
        <div className="tv-flex tv-gap-1 tv-items-center tv-justify-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              dispatch(setSelectedTicker(stock.ticker));
              dispatch(setChartTicker(stock.ticker));
              dispatch(setDeepAnalysisReturnTab('screening'));
              dispatch(requestDeepSubTab('analysis'));
              dispatch(requestNavigateMainTab('deep_analysis'));
            }}
            className="btn btn-sm inline-flex items-center justify-center min-h-[32px] px-3 py-1.5"
            style={{
              fontSize: '0.5625rem',
              backgroundColor: '#d4a853',
              color: '#1a1a2e',
              border: 'none',
              borderRadius: '3px',
              fontWeight: 600,
            }}
            title={t('resultsTableVirtualized.openInDeepAnalysis', { defaultValue: 'Open in Deep Analysis' })}
          >
            {t('resultsTableVirtualized.deep', { defaultValue: 'Deep' })}
          </button>
        </div>
      );
    },
    [dispatch, t]
  );

  // Enhanced checkbox handler to support Shift-click range selection
  const handleCheckboxInteraction = useCallback(
    (
      ticker: string,
      index: number,
      isChecked: boolean,
      isShiftKey: boolean
    ) => {
      setSelectedStocks((prev) => {
        const next = new Set(prev);

        if (isShiftKey && lastSelectedIndex !== -1 && stocks.length > 0) {
          const start = Math.min(lastSelectedIndex, index);
          const end = Math.max(lastSelectedIndex, index);
          for (let i = start; i <= end; i++) {
            const t = stocks[i]?.ticker;
            if (t) {
              next.add(t);
            }
          }
        } else {
          if (isChecked) {
            next.add(ticker);
          } else {
            next.delete(ticker);
          }
        }

        return next;
      });

      setLastSelectedIndex(index);
    },
    [lastSelectedIndex, stocks]
  );

  // MA distance %: read the backend-computed ma_*_distance_pct (close-based, and
  // already reflects ma_eval_price in WEEKLY mode - see technical_analysis.py).
  // Do NOT re-derive from current_price: that is now the LIVE price and would
  // diverge from the screened distance. Missing -> 0.
  const maDistancePct = useCallback(
    (stock: StockData, maKey: 'ma_50' | 'ma_100' | 'ma_150' | 'ma_200'): number => {
      const pct = stock[`${maKey}_distance_pct` as keyof StockData];
      return pct != null && !Number.isNaN(Number(pct)) ? Number(pct) : 0;
    },
    []
  );

  const columns: TableColumn[] = useMemo(
    () => [
      {
        key: 'select' as keyof StockData,
        label: '',
        width: 50,
        minWidth: 50,
        flex: 0,
        sortable: false,
        render: (value: any, stock: StockData, index: number) => (
          <div onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selectedStocks.has(stock.ticker)}
              onClick={(e) => {
                e.stopPropagation();
                const willBeChecked = !selectedStocks.has(stock.ticker);
                const shift = (
                  e as unknown as React.MouseEvent<HTMLInputElement>
                ).shiftKey;
                handleCheckboxInteraction(
                  stock.ticker,
                  index,
                  willBeChecked,
                  !!shift
                );
              }}
              onChange={(e) => {
                const shiftKey =
                  (e as unknown as React.ChangeEvent<HTMLInputElement>)
                    .nativeEvent && (e as any).nativeEvent.shiftKey;
                handleCheckboxInteraction(
                  stock.ticker,
                  index,
                  e.target.checked,
                  !!shiftKey
                );
              }}
              aria-label={t('table.tbl.selectTicker', { defaultValue: 'Select {{ticker}}', ticker: stock.ticker })}
              title={t('table.tbl.selectTicker', { defaultValue: 'Select {{ticker}}', ticker: stock.ticker })}
              className="tv-form-check-input"
            />
          </div>
        ),
      },
      {
        key: 'ticker',
        label: t('resultsTableVirtualized.colTicker', { defaultValue: 'Ticker' }),
        width: 120,
        minWidth: 100,
        flex: 1,
        sortable: true,
        render: (value: any, stock: StockData) => {
          const hasSupportLine =
            stock.strong_support_result?.detected &&
            stock.strong_support_result?.support_line_coords;

          return (
            <div className="tv-flex tv-items-center tv-gap-2">
              <div className="tv-flex tv-flex-col tv-gap-1">
                <div className="tv-flex tv-items-center tv-gap-2">
                  <span
                    className="tv-font-mono tv-font-semibold"
                    style={{
                      color: 'var(--tv-text-primary)',
                      fontSize: '0.875rem',
                      fontWeight: '700',
                    }}
                  >
                    {stock.ticker}
                  </span>
                  {stock.exchange && (
                    <span
                      className="tv-badge tv-text-xs"
                      style={{
                        // Explicit, theme-independent chip colors: each pill
                        // carries its own background so contrast holds in both
                        // light/dark. (Previously used undefined --tv-bull /
                        // --tv-secondary vars → white-on-white in light theme.)
                        backgroundColor:
                          stock.exchange === 'NYSE'
                            ? '#1d4ed8'
                            : stock.exchange === 'NASDAQ'
                              ? 'var(--accent-primary)'
                              : '#475569',
                        color:
                          stock.exchange === 'NASDAQ'
                            ? 'var(--on_primary)'
                            : 'white',
                        fontWeight: '600',
                        padding: '2px 5px',
                        borderRadius: '3px',
                        fontSize: '0.6875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        whiteSpace: 'nowrap',
                        lineHeight: 1,
                      }}
                      title={stock.exchange}
                    >
                      {stock.exchange === 'NASDAQ' ? 'NDQ' : stock.exchange}
                    </span>
                  )}
                </div>
                {stock.company_name && (
                  <div
                    className="tv-text-xs"
                    style={{
                      color: 'var(--tv-text-secondary)',
                      fontSize: '0.6875rem',
                      fontWeight: '400',
                      marginTop: '2px',
                    }}
                    title={stock.company_name}
                  >
                    {stock.company_name}
                  </div>
                )}
                {hasSupportLine && stock.strong_support_result && (
                  <div
                    className="tv-badge tv-text-xs tv-font-semibold"
                    style={{
                      backgroundColor: '#17a2b8',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '8px',
                      fontSize: '0.5625rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '3px',
                      width: 'fit-content',
                      cursor: 'help',
                    }}
                    title={`${t('resultsTableVirtualized.supportLineDetected', { defaultValue: 'Support Line Detected' })}
${t('resultsTableVirtualized.typeLabel', { defaultValue: 'Type:' })} ${
                      stock.strong_support_result.support_type
                        .charAt(0)
                        .toUpperCase() +
                      stock.strong_support_result.support_type.slice(1)
                    }
${t('resultsTableVirtualized.touchPoints', { defaultValue: 'Touch Points:' })} ${stock.strong_support_result.touches}
${t('resultsTableVirtualized.supportLevel', { defaultValue: 'Support Level:' })} $${stock.strong_support_result.support_level.toFixed(2)}
${t('resultsTableVirtualized.bounceStrength', { defaultValue: 'Bounce Strength:' })} ${stock.strong_support_result.strength.toFixed(0)}/100${
                      stock.strong_support_result.support_slope
                        ? `
${t('resultsTableVirtualized.slopeLabel', { defaultValue: 'Slope:' })} ${stock.strong_support_result.support_slope.toFixed(4)}`
                        : ''
                    }
${t('resultsTableVirtualized.clickRowToViewSupport', { defaultValue: 'Click row to view support line on chart' })}`}
                  >
                    <i
                      className="fas fa-chart-line"
                      style={{ fontSize: '0.5rem' }}
                    ></i>
                    {t('resultsTableVirtualized.supportLine', { defaultValue: 'Support Line' })}
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      // Score column - now second column after Ticker
      {
        key: 'score',
        label: t('resultsTableVirtualized.colScore', { defaultValue: 'Score' }),
        width: 70,
        minWidth: 60,
        flex: 0.6,
        sortable: true,
        render: (value: any, stock: StockData) => {
          const score = stock.score;
          if (
            score === undefined ||
            score === null ||
            Number.isNaN(Number(score))
          ) {
            return <span className="tv-text-secondary">-</span>;
          }
          const numScore = Number(score);
          // Color coding: 7-10 green, 4-6 yellow, 1-3 red
          const scoreClass =
            numScore >= 7
              ? 'tv-text-bull'
              : numScore >= 4
                ? 'tv-text-warning'
                : 'tv-text-bear';
          // Build tooltip with breakdown and factor-aware explanation
          const breakdown = stock.score_breakdown;

          // Cup-and-Handle scoring mode: cup component breakdown (0..1 values)
          // plus pattern quality / status / target essentials. Legacy
          // (standard) rendering below is left unchanged.
          if (stock.score_mode === 'entry_quality') {
            return renderEntryQualityScore(stock, numScore, scoreClass);
          }
          if (stock.score_mode === 'cup_handle') {
            return renderCupHandleScore(stock, numScore, scoreClass);
          }
          if (stock.score_mode === 'darvas_box') {
            return renderDarvasScore(stock, numScore, scoreClass);
          }

          let tooltipText: string;
          if (breakdown && typeof breakdown === 'object') {
            const n = (x: unknown) =>
              typeof x === 'number' && !Number.isNaN(x) ? x : 0;
            const bk = breakdown as Record<string, unknown>;
            const ma = n(bk['ma_proximity']);
            const maSupport = n(bk['ma_support'] || bk['ma_cross_stability']);
            const rsi = n(bk['rsi']);
            const stoch = n(bk['stochastic']);
            const trend = n(bk['trend']);
            const vol = n(bk['volume']);
            const reversalCandle = n(bk['reversal_candle']);

            const factorEntries: Array<{ label: string; value: number }> = [
              { label: t('table.tbl.factorMaDist', { defaultValue: 'MA Dist' }), value: ma },
              { label: t('table.tbl.factorMaSupport', { defaultValue: 'MA Support' }), value: maSupport },
              { label: t('table.tbl.factorRsi', { defaultValue: 'RSI' }), value: rsi },
              { label: t('table.tbl.factorStoch', { defaultValue: 'Stoch' }), value: stoch },
              { label: t('table.tbl.factorTrend', { defaultValue: 'Trend' }), value: trend },
              { label: t('table.tbl.factorVol', { defaultValue: 'Vol' }), value: vol },
            ];

            const maSupportLabel = t('table.tbl.factorMaSupport', { defaultValue: 'MA Support' });
            if (reversalCandle > 0) {
              factorEntries.push({ label: t('table.tbl.factorReversal', { defaultValue: 'Reversal' }), value: reversalCandle });
            }

            const strongDriversRaw = factorEntries.filter((f) => f.value >= 70);
            let strongDrivers = strongDriversRaw.map((f) => f.label).join(', ');
            if (
              strongDriversRaw.find((f) => f.label === maSupportLabel) &&
              maSupport >= 70
            ) {
              strongDrivers += ' ' + t('table.tbl.priceHeldAboveMa150', { defaultValue: '(price held above MA150!)' });
            }
            const weakDrivers = factorEntries
              .filter((f) => f.value < 40)
              .map((f) => f.label)
              .join(', ');

            let qualitative: string;
            if (numScore >= 7) {
              qualitative = strongDrivers
                ? t('table.tbl.scoreHighDriven', { defaultValue: 'High score driven by strong {{drivers}}.', drivers: strongDrivers })
                : t('table.tbl.scoreHighAligned', { defaultValue: 'High score: most technical factors are well aligned.' });
            } else if (numScore >= 4) {
              if (strongDrivers && weakDrivers) {
                qualitative = t('table.tbl.scoreMixedStrongWeak', { defaultValue: 'Mixed score: {{strong}} are strong, but {{weak}} are weak.', strong: strongDrivers, weak: weakDrivers });
              } else if (strongDrivers) {
                qualitative = t('table.tbl.scoreMixedStrong', { defaultValue: 'Mixed score with support from {{drivers}}.', drivers: strongDrivers });
              } else if (weakDrivers) {
                qualitative = t('table.tbl.scoreMixedWeak', { defaultValue: 'Mixed score held back by weak {{drivers}}.', drivers: weakDrivers });
              } else {
                qualitative =
                  t('table.tbl.scoreMixedNeutral', { defaultValue: 'Mixed score: some factors support the setup, others are neutral.' });
              }
            } else {
              qualitative = weakDrivers
                ? t('table.tbl.scoreLowWeak', { defaultValue: 'Low score: weak {{drivers}} are dragging the setup down.', drivers: weakDrivers })
                : t('table.tbl.scoreLowUnconfirmed', { defaultValue: 'Low score: key technical factors are not confirming the setup.' });
            }

            const numericSummary =
              reversalCandle > 0
                ? `MA Dist: ${ma.toFixed(0)}, MA Sup: ${maSupport.toFixed(0)}, RSI: ${rsi.toFixed(0)}, Stoch: ${stoch.toFixed(0)}, Trend: ${trend.toFixed(0)}, Vol: ${vol.toFixed(0)}, Reversal: ${reversalCandle.toFixed(0)}`
                : `MA Dist: ${ma.toFixed(0)}, MA Support: ${maSupport.toFixed(0)}, RSI: ${rsi.toFixed(0)}, Stoch: ${stoch.toFixed(0)}, Trend: ${trend.toFixed(0)}, Vol: ${vol.toFixed(0)}`;

            const scoringMode = reversalCandle > 0 ? '\n' + t('table.tbl.smoothScoring', { defaultValue: '[Smooth Scoring]' }) : '';
            tooltipText = `${qualitative}\n${numericSummary}${scoringMode}`;
          } else {
            tooltipText = t('table.tbl.scoreLabel', { defaultValue: 'Score: {{score}}', score: numScore.toFixed(1) });
          }
          return (
            <span
              className={`tv-font-bold ${scoreClass} cursor-help`}
              title={tooltipText}
            >
              {formatScore(numScore)}
            </span>
          );
        },
      },
      // Entry Status column - rendered immediately after Score so buy-readiness
      // sits next to the conviction number.
      {
        key: 'entry_status',
        label: t('resultsTableVirtualized.colEntryStatus', { defaultValue: 'Entry Status' }),
        width: 130,
        minWidth: 110,
        flex: 1,
        sortable: true,
        // "Best entry" sorts to top - full graded rank per
        // SPEC_ENTRY_CONVICTION_2026-06-19 §1/F-C3:
        // strong_buy=6 > buy=5 > accumulate=4 > wait_for_reversal=3 >
        // holding=2 > extended=1 > below_stop=0 > unknown=-1. The deprecated
        // `in_buy_zone` alias ranks alongside accumulate (4).
        sortValue: (value: any, stock: StockData) => {
          switch (stock.entry_status) {
            case 'strong_buy':
              return 6;
            case 'buy':
              return 5;
            case 'accumulate':
              return 4;
            case 'in_buy_zone': // deprecated alias
              return 4;
            case 'wait_for_reversal':
              return 3;
            case 'holding':
              return 2;
            case 'extended':
              return 1;
            case 'below_stop':
              return 0;
            default:
              return -1;
          }
        },
        render: (value: any, stock: StockData) => renderEntryStatus(stock),
      },
      // Hidden: Stock Type column - data not currently populated (BUG-006)
      // {
      //   key: 'stock_type' as keyof StockData,
      //   label: 'Stock Type',
      //   width: 120,
      //   minWidth: 100,
      //   flex: 1,
      //   sortable: true,
      //   render: (value: any, stock: StockData) => renderStockType(stock),
      // },
      {
        key: 'current_price',
        label: t('resultsTableVirtualized.colPrice', { defaultValue: 'Price' }),
        width: 100,
        minWidth: 80,
        flex: 1,
        sortable: true,
        render: (value: any, stock: StockData) => {
          return (
            <div className="tv-flex tv-flex-col">
              <span>${Number(value).toFixed(2)}</span>
            </div>
          );
        },
      },
      {
        key: 'day_change_percent',
        label: t('resultsTableVirtualized.colDayPct', { defaultValue: 'Day %' }),
        width: 90,
        minWidth: 70,
        flex: 0.8,
        sortable: true,
        render: (value: any) => {
          const changeValue = Number(value) || 0;
          return (
            <span
              className={changeValue >= 0 ? 'tv-text-bull' : 'tv-text-bear'}
            >
              {changeValue >= 0 ? '+' : ''}
              {changeValue.toFixed(2)}%
            </span>
          );
        },
      },
      {
        key: 'market_cap',
        label: t('resultsTableVirtualized.colMarketCap', { defaultValue: 'Market Cap' }),
        width: 120,
        minWidth: 90,
        flex: 1,
        sortable: true,
        render: (value: any) => (
          <span
            style={{
              // #6B7280 clears WCAG AA (~4.5:1) on both light and dark row
              // backgrounds; #9E9E9E failed (~2.7:1) on the light theme.
              color: '#6B7280',
              fontFamily: 'Consolas, "Courier New", monospace',
              fontSize: '0.75rem',
              fontWeight: '400',
            }}
          >
            {formatMarketCap(value)}
          </span>
        ),
      },
      {
        key: 'ma_50',
        label: t('resultsTableVirtualized.col50dMaPct', { defaultValue: '50D MA %' }),
        width: 100,
        minWidth: 80,
        flex: 0.8,
        sortable: true,
        sortValue: (value: any, stock: StockData) => {
          return maDistancePct(stock, 'ma_50');
        },
        render: (value: any, stock: StockData) => {
          const percentDiff = maDistancePct(stock, 'ma_50');
          return (
            <span
              className={percentDiff >= 0 ? 'tv-text-bull' : 'tv-text-bear'}
            >
              {percentDiff >= 0 ? '+' : ''}
              {percentDiff.toFixed(1)}%
            </span>
          );
        },
      },
      {
        key: 'ma_100',
        label: t('resultsTableVirtualized.col100dMaPct', { defaultValue: '100D MA %' }),
        width: 100,
        minWidth: 80,
        flex: 0.8,
        sortable: true,
        sortValue: (value: any, stock: StockData) => {
          return maDistancePct(stock, 'ma_100');
        },
        render: (value: any, stock: StockData) => {
          if (!value) {
            return (
              <span className="tv-text-muted" style={{ fontSize: '0.75rem' }}>
                N/A
              </span>
            );
          }
          const percentDiff = maDistancePct(stock, 'ma_100');
          return (
            <div className="tv-flex tv-flex-col tv-items-start">
              <span
                className={percentDiff >= 0 ? 'tv-text-bull' : 'tv-text-bear'}
              >
                {percentDiff >= 0 ? '+' : ''}
                {percentDiff.toFixed(1)}%
              </span>
              {stock.near_100 && (
                <span
                  className="tv-badge tv-text-xs"
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    fontSize: '0.5625rem',
                    marginTop: '2px',
                  }}
                  title={t('resultsTableVirtualized.priceNearMa100', { defaultValue: 'Price is near MA 100' })}
                >
                  ✓ {t('resultsTableVirtualized.near', { defaultValue: 'NEAR' })}
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: 'ma_150',
        label: t('resultsTableVirtualized.col150dMa', { defaultValue: '150D MA' }),
        width: 100,
        minWidth: 80,
        flex: 0.8,
        sortable: true,
        sortValue: (value: any, stock: StockData) => {
          return maDistancePct(stock, 'ma_150');
        },
        render: (value: any, stock: StockData) => {
          const percentDiff = maDistancePct(stock, 'ma_150');
          return (
            <span
              className={percentDiff >= 0 ? 'tv-text-bull' : 'tv-text-bear'}
            >
              {percentDiff >= 0 ? '+' : ''}
              {percentDiff.toFixed(1)}%
            </span>
          );
        },
      },
      {
        key: 'ma_200',
        label: t('resultsTableVirtualized.col200dMa', { defaultValue: '200D MA' }),
        width: 100,
        minWidth: 80,
        flex: 0.8,
        sortable: true,
        sortValue: (value: any, stock: StockData) => {
          return maDistancePct(stock, 'ma_200');
        },
        render: (value: any, stock: StockData) => {
          const percentDiff = maDistancePct(stock, 'ma_200');
          return (
            <span
              className={percentDiff >= 0 ? 'tv-text-bull' : 'tv-text-bear'}
            >
              {percentDiff >= 0 ? '+' : ''}
              {percentDiff.toFixed(1)}%
            </span>
          );
        },
      },
      // Golden Cross column - disabled
      // {
      //   key: 'golden_cross_result' as keyof StockData,
      //   label: '🌟 Golden Cross',
      //   width: 150,
      //   minWidth: 120,
      //   flex: 1.2,
      //   sortable: false,
      //   render: (value: any, stock: StockData) =>
      //     renderGoldenCrossResult(stock),
      // },
      // Strong Support column - disabled
      // {
      //   key: 'strong_support_result' as keyof StockData,
      //   label: '🎯 Strong Support',
      //   width: 200,
      //   minWidth: 180,
      //   flex: 1.5,
      //   sortable: false,
      //   render: (value: any, stock: StockData) =>
      //     renderStrongSupportResult(stock),
      // },
      {
        key: 'atr_percentage',
        label: t('resultsTableVirtualized.colAtrPct', { defaultValue: 'ATR %' }),
        width: 80,
        minWidth: 60,
        flex: 0.7,
        sortable: true,
        render: (value: any) => `${Number(value).toFixed(2)}%`,
      },
      {
        key: 'target_percent',
        label: t('resultsTableVirtualized.colTargetPct', { defaultValue: 'Target %' }),
        width: 90,
        minWidth: 70,
        flex: 0.8,
        sortable: true,
        render: (value: any) => (
          <span
            className={`tv-font-medium ${
              Number(value) >= 10
                ? 'tv-text-bull'
                : Number(value) >= 5
                  ? 'tv-text-warning'
                  : 'tv-text-secondary'
            }`}
          >
            {Number(value).toFixed(1)}%
          </span>
        ),
      },
      {
        key: 'blended_rr',
        label: t('resultsTableVirtualized.colBlendedRr', { defaultValue: 'Blended R:R' }),
        headerTitle: t('resultsTableVirtualized.blendedRrTitle', { defaultValue: 'Structural: no fees, no tax, default 50/50 exits' }),
        width: 100,
        minWidth: 80,
        flex: 0.8,
        sortable: true,
        render: (value: any) =>
          value == null ? '-' : `${Number(value).toFixed(2)}:1`,
      },
      {
        key: 'entry_rationale',
        label: t('resultsTableVirtualized.colEntrySignal', { defaultValue: 'Entry Signal' }),
        minWidth: 150,
        flex: 2,
        sortable: true,
        render: (value: any, stock: StockData) => renderEntryRationale(stock),
      },
      // Gap Down column - disabled
      // {
      //   key: 'gap_down_result' as keyof StockData,
      //   label: 'Gap Down',
      //   width: 120,
      //   minWidth: 100,
      //   flex: 1.2,
      //   sortable: false,
      //   render: (value: any, stock: StockData) => renderGapDownResult(stock),
      // },
      ...(rsiRecoveryEnabled
        ? [
            {
              key: 'current_rsi' as keyof StockData,
              label: t('table.tbl.colRsi', { defaultValue: 'RSI' }),
              width: 100,
              minWidth: 80,
              flex: 0.8,
              sortable: true,
              render: (value: any, stock: StockData) => renderCurrentRSI(stock),
            },
          ]
        : []),
      // AI Buy Signal column - disabled (merged into AI Analysis)
      // ...(buySignalsEnabled
      //   ? [
      //       {
      //         key: 'ai_buy_signal' as keyof StockData,
      //         label: 'AI Buy Signal',
      //         width: 120,
      //         minWidth: 100,
      //         flex: 1.2,
      //         sortable: false,
      //         render: (value: any, stock: StockData) =>
      //           renderAIBuySignal(stock),
      //       },
      //     ]
      //   : []),
      // Combined AI Analysis column (Grok + Perplexity)
      {
        key: 'ai_analysis' as keyof StockData,
        label: t('table.tbl.colAi', { defaultValue: 'AI' }),
        width: 100,
        minWidth: 90,
        flex: 0.8,
        sortable: false,
        render: (value: any, stock: StockData) => renderAIAnalysis(stock),
      },
      // Grok Sentiment column - disabled (merged into AI Analysis)
      // {
      //   key: 'grok_sentiment' as keyof StockData,
      //   label: 'Grok Sentiment',
      //   width: 130,
      //   minWidth: 120,
      //   flex: 1,
      //   sortable: false,
      //   render: (value: any, stock: StockData) => renderGrokSentiment(stock),
      // },
      // Perplexity Analysis column - disabled (merged into AI Analysis)
      // {
      //   key: 'perplexity_analysis' as keyof StockData,
      //   label: 'Perplexity Analysis',
      //   width: 150,
      //   minWidth: 130,
      //   flex: 1,
      //   sortable: false,
      //   render: (value: any, stock: StockData) => renderPerplexityAnalysis(stock),
      // },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      buySignalsEnabled,
      rsiRecoveryEnabled,
      formatNumber,
      formatMarketCap,
      renderEntryRationale,
      renderGapDownResult,
      renderCurrentRSI,
      renderAIBuySignal,
      renderGoldenCrossResult,
      renderStrongSupportResult,
      renderAIAnalysis,
      selectedStocks,
      handleCheckboxInteraction,
      maDistancePct,
      t,
    ]
  );

  const handleSort = useCallback(
    (column: keyof StockData) => {
      const direction =
        sortConfig.column === (column as string) &&
        sortConfig.direction === 'asc'
          ? 'desc'
          : 'asc';
      dispatch(setSortConfig({ column: column as string, direction }));
    },
    [sortConfig.column, sortConfig.direction, dispatch]
  );

  const handleSelectAll = useCallback(() => {
    setSelectedStocks(new Set(stocks.map((stock: StockData) => stock.ticker)));
  }, [stocks]);

  const handleUnselectAll = useCallback(() => {
    setSelectedStocks(new Set());
  }, []);

  const copySelectedToClipboard = useCallback(async () => {
    if (selectedVisibleCount === 0) {
      dispatch(
        showGlobalMessage({
          message: t('resultsTableVirtualized.selectAtLeastOneCopy', { defaultValue: 'Please select at least one ticker to copy' }),
          type: 'warning',
        })
      );
      return;
    }

    try {
      // Copy only the selected tickers within the current filtered view, so the
      // copied list matches the "(N)" shown on the button (Selected ≤ All).
      const tickersList = stocks
        .filter((s: StockData) => selectedStocks.has(s.ticker))
        .map((s: StockData) => s.ticker)
        .join(', ');
      await navigator.clipboard.writeText(tickersList);
      flashExportFeedback('selected');
      dispatch(
        showGlobalMessage({
          message: t('resultsTableVirtualized.copiedTickers', { defaultValue: 'Copied {{count}} tickers to clipboard', count: selectedVisibleCount }),
          type: 'success',
        })
      );
    } catch (error) {
      console.error('Clipboard copy error:', error);
      dispatch(
        showGlobalMessage({
          message: t('resultsTableVirtualized.failedToCopy', { defaultValue: 'Failed to copy tickers' }),
          type: 'danger',
        })
      );
    }
  }, [selectedStocks, stocks, selectedVisibleCount, dispatch, flashExportFeedback, t]);

  const exportAllTickersToClipboard = useCallback(async () => {
    if (!stocks.length) {
      dispatch(
        showGlobalMessage({
          message: t('resultsTableVirtualized.noTickersToCopy', { defaultValue: 'No tickers available to copy' }),
          type: 'warning',
        })
      );
      return;
    }

    try {
      const tickersList = stocks.map((s: StockData) => s.ticker).join(', ');
      await navigator.clipboard.writeText(tickersList);
      flashExportFeedback('all');
      dispatch(
        showGlobalMessage({
          message: t('resultsTableVirtualized.copiedTickers', { defaultValue: 'Copied {{count}} tickers to clipboard', count: stocks.length }),
          type: 'success',
        })
      );
    } catch (error) {
      console.error('Clipboard export error:', error);
      dispatch(
        showGlobalMessage({
          message: t('resultsTableVirtualized.failedToCopy', { defaultValue: 'Failed to copy tickers' }),
          type: 'danger',
        })
      );
    }
  }, [stocks, dispatch, flashExportFeedback, t]);

  const exportSelectedTickers = useCallback(async () => {
    if (selectedVisibleCount === 0) {
      dispatch(
        showGlobalMessage({
          message: t('resultsTableVirtualized.selectAtLeastOneExport', { defaultValue: 'Please select at least one ticker to export' }),
          type: 'warning',
        })
      );
      return;
    }

    setIsExporting(true);
    try {
      const selectedStockData = stocks.filter((stock: StockData) =>
        selectedStocks.has(stock.ticker)
      );

      // Create CSV content
      const headers = [
        t('resultsTableVirtualized.csvTicker', { defaultValue: 'Ticker' }),
        t('resultsTableVirtualized.csvPrice', { defaultValue: 'Price' }),
        t('resultsTableVirtualized.csvMarketCap', { defaultValue: 'Market Cap' }),
        t('resultsTableVirtualized.csv50dMa', { defaultValue: '50D MA' }),
        t('resultsTableVirtualized.csv100dMa', { defaultValue: '100D MA' }),
        t('resultsTableVirtualized.csv150dMa', { defaultValue: '150D MA' }),
        t('resultsTableVirtualized.csv200dMa', { defaultValue: '200D MA' }),
        t('resultsTableVirtualized.csvAtrPct', { defaultValue: 'ATR %' }),
        t('resultsTableVirtualized.csvTargetPct', { defaultValue: 'Target %' }),
        t('resultsTableVirtualized.csvEntrySignal', { defaultValue: 'Entry Signal' }),
      ];
      const rows = selectedStockData.map((stock: StockData) => [
        stock.ticker,
        stock.current_price?.toFixed(2) || '',
        stock.market_cap?.toString() || '',
        stock.ma_50?.toFixed(2) || '',
        stock.ma_100?.toFixed(2) || '',
        stock.ma_150?.toFixed(2) || '',
        stock.ma_200?.toFixed(2) || '',
        stock.atr_percentage?.toFixed(2) || '',
        stock.target_percent?.toFixed(1) || '',
        stock.entry_rationale || '',
      ]);

      const csvContent = [headers, ...rows]
        .map((row: string[]) =>
          row.map((cell: string) => `"${cell}"`).join(',')
        )
        .join('\n');

      // Download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `selected_tickers_${new Date().toISOString().split('T')[0]}.csv`
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      flashExportFeedback('csv');
      dispatch(
        showGlobalMessage({
          message: t('resultsTableVirtualized.exportedToCsv', { defaultValue: 'Exported {{count}} selected tickers to CSV', count: selectedStockData.length }),
          type: 'success',
        })
      );
    } catch (error) {
      console.error('Export error:', error);
      dispatch(
        showGlobalMessage({
          message: t('resultsTableVirtualized.failedToExport', { defaultValue: 'Failed to export selected tickers' }),
          type: 'danger',
        })
      );
    } finally {
      setIsExporting(false);
    }
  }, [selectedStocks, stocks, selectedVisibleCount, dispatch, flashExportFeedback, t]);

  const handleRowClick = useCallback(
    (stock: StockData, event: React.MouseEvent, index: number) => {
      // Don't trigger if clicking on checkbox, buttons, or favorite indicator
      const target = event.target as HTMLElement;
      if (
        target.closest('input[type="checkbox"]') ||
        target.closest('button') ||
        target.closest('.favorite-indicator')
      ) {
        return;
      }

      // Selection logic with modifiers
      const isShift = event.shiftKey;
      const isToggle = event.ctrlKey || event.metaKey;

      setCurrentRowIndex(index);

      if (isShift) {
        setSelectedStocks((prev) => {
          const next = new Set(prev);
          const start =
            lastSelectedIndex !== -1
              ? Math.min(lastSelectedIndex, index)
              : index;
          const end =
            lastSelectedIndex !== -1
              ? Math.max(lastSelectedIndex, index)
              : index;
          for (let i = start; i <= end; i++) {
            const t = stocks[i]?.ticker;
            if (t) next.add(t);
          }
          return next;
        });
        setLastSelectedIndex(index);
        return; // Don't open panel on range selection
      }

      if (isToggle) {
        setSelectedStocks((prev) => {
          const next = new Set(prev);
          if (next.has(stock.ticker)) {
            next.delete(stock.ticker);
          } else {
            next.add(stock.ticker);
          }
          return next;
        });
        setLastSelectedIndex(index);
        return; // Don't open panel when toggling selection
      }

      // Normal click: select single row and open analysis panel
      setSelectedStocks(new Set([stock.ticker]));
      setLastSelectedIndex(index);

      // Use startTransition for better responsiveness
      startTransition(() => {
        dispatch(openPanel({ ticker: stock.ticker }));
      });
    },
    [dispatch, lastSelectedIndex, stocks]
  );

  const itemKey = useCallback(
    (index: number) => stocks[index]?.ticker || `row-${index}`,
    [stocks]
  );

  // Show loading state
  if (loading) {
    return (
      <div className="tv-card">
        <LoadingSpinner size="lg" text={t('table.tbl.loadingStockData', { defaultValue: 'Loading stock data...' })} centered />
      </div>
    );
  }

  // Screening progress bar (rendered above the table, not replacing it)
  const isScreening =
    screeningStatus.status === 'running' ||
    screeningStatus.status === 'loading';
  const screeningProgressBar = isScreening
    ? (() => {
        const processed = screeningStatus.processed_count || 0;
        const total = screeningStatus.total_stocks || 0;
        const passing = screeningStatus.passing_count || 0;
        const progressPct =
          total > 0 ? Math.round((processed / total) * 100) : 0;
        const currentStock = screeningStatus.current_stock || '';

        return (
          <div
            style={{
              padding: '10px 16px',
              marginBottom: '8px',
              borderRadius: '8px',
              backgroundColor: 'var(--tv-card-bg, #1e222d)',
              border: '1px solid var(--tv-border-color, #363a45)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '6px',
              }}
            >
              <div
                className="animate-spin"
                style={{
                  width: '16px',
                  height: '16px',
                  flexShrink: 0,
                  border: '2px solid var(--tv-border-color, #363a45)',
                  borderTopColor: 'var(--tv-blue, #2962ff)',
                  borderRadius: '50%',
                }}
              />
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span
                  style={{
                    color: 'var(--tv-text-primary)',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                  }}
                >
                  {t('resultsTableVirtualized.screeningProgress', { defaultValue: 'Screening: {{processed}} / {{total}}', processed: processed.toLocaleString(), total: total.toLocaleString() })}
                  {passing > 0 && (
                    <span style={{ color: '#26a69a', marginLeft: '8px' }}>
                      {t('resultsTableVirtualized.found', { defaultValue: '{{count}} found', count: passing })}
                    </span>
                  )}
                </span>
                <span
                  style={{
                    color: 'var(--tv-text-secondary)',
                    fontSize: '0.75rem',
                  }}
                >
                  {progressPct}%{currentStock && ` · ${currentStock}`}
                </span>
              </div>
            </div>
            {total > 0 && (
              <div
                style={{
                  width: '100%',
                  height: '4px',
                  backgroundColor: 'var(--tv-border-color, #363a45)',
                  borderRadius: '2px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${progressPct}%`,
                    height: '100%',
                    backgroundColor: 'var(--tv-blue, #2962ff)',
                    borderRadius: '2px',
                    transition: 'width 0.5s ease',
                  }}
                />
              </div>
            )}
          </div>
        );
      })()
    : null;

  // Non-blocking banner shown when the backend fell back to the unfiltered
  // universe because `index_membership` is empty. Stays visible until the
  // next screening run clears it on the backend side. Pure render - no
  // dismiss button on purpose; the message is actionable for ops, not users.
  const indexFilterWarning = screeningStatus.index_filter_warning;
  const indexFilterBanner = indexFilterWarning ? (
    <div
      role="alert"
      style={{
        padding: '10px 14px',
        marginBottom: '8px',
        borderRadius: '8px',
        backgroundColor: 'rgba(242, 153, 74, 0.12)',
        border: '1px solid rgba(242, 153, 74, 0.4)',
        color: 'var(--tv-text-primary)',
        fontSize: '0.8125rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: '1rem', lineHeight: 1 }}>
        ⚠
      </span>
      <span style={{ flex: 1 }}>{indexFilterWarning}</span>
    </div>
  ) : null;

  // Show empty state ONLY if there are no stocks and screening is not running
  if (allStocks.length === 0 && !isScreening) {
    return (
      <div
        className={cn(
          'tv-card',
          'max-md:rounded-t-2xl max-md:rounded-b-none max-md:border-x-0 max-md:-mx-3 max-md:border-t max-md:shadow-none max-md:border-border-subtle'
        )}
      >
        <EmptyState
          icon="fas fa-chart-line"
          title={t('resultsTableVirtualized.noStocksFound', { defaultValue: 'No Stocks Found' })}
          description={t('resultsTableVirtualized.noStocksDescription', { defaultValue: 'Run the screening process to discover stocks that match your criteria.' })}
          action={{
            label: t('resultsTableVirtualized.screenStocks', { defaultValue: 'Screen Stocks' }),
            onClick: () => dispatch(screenStocksThunk()),
          }}
        />
        {/* Mobile-only quick actions when no results yet */}
      </div>
    );
  }

  return (
    <>
      {indexFilterBanner}
      {screeningProgressBar}
      <div className="flex w-full min-w-0 min-h-0 flex-1 flex-col">
        <div className="flex w-full min-w-0 min-h-0 flex-1 flex-col">
          <div
            className={cn(
              'tv-card flex min-h-0 min-w-0 flex-1 flex-col max-md:overflow-hidden md:overflow-visible',
              'max-md:-mx-3 max-md:rounded-t-2xl max-md:rounded-b-none max-md:border-x-0 max-md:border-t max-md:shadow-none max-md:border-border-subtle'
            )}
          >
            {/* Header - stacked on mobile, row on desktop - sticky on desktop */}
            <div
              className="tv-card-header flex-shrink-0 md:px-3 md:py-2.5 max-md:px-3 max-md:pt-2 max-md:pb-2 sticky top-0 z-10"
              style={{
                backgroundColor: 'var(--surface_container_high)',
                color: 'var(--text-primary)',
                borderLeft: '3px solid var(--accent-primary)',
                borderRadius: 'var(--tv-radius-lg) var(--tv-radius-lg) 0 0',
              }}
            >
              <div className="flex flex-col gap-2.5 md:flex-row md:items-center md:justify-between md:gap-2 md:flex-wrap">
                {/* Title + summary - compact single-line meta on mobile */}
                <div className="flex items-start gap-2 min-w-0">
                  <div
                    className="rounded-lg flex-shrink-0 flex items-center justify-center max-md:w-9 max-md:h-9 md:w-8 md:h-8"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.18)' }}
                  >
                    <i className="fas fa-table text-text-primary text-sm md:text-xs" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h5 className="text-text-primary font-semibold m-0 leading-tight max-md:text-base md:text-[15px]">
                        {t('resultsTableVirtualized.screeningResults', { defaultValue: 'Screening Results' })}
                      </h5>
                      {/* Real-time indicator */}
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                        style={{
                          backgroundColor: 'rgba(34, 197, 94, 0.2)',
                          color: '#22c55e',
                          border: '1px solid rgba(34, 197, 94, 0.3)',
                        }}
                        title={t('resultsTableVirtualized.realTimeData', { defaultValue: 'Real-time market data' })}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                        {t('resultsTableVirtualized.live', { defaultValue: 'Live' })}
                      </span>
                    </div>
                    <div className="text-text-secondary text-xs md:text-sm mt-0.5 leading-snug flex flex-wrap items-baseline gap-x-1.5 max-md:gap-x-1">
                      <span className="md:hidden">
                        {isScreening
                          ? t('resultsTableVirtualized.scanning', { defaultValue: 'Scanning... {{count}} so far', count: stocks.length })
                          : t('resultsTableVirtualized.stocksCount', { defaultValue: '{{count}} stocks', count: stocks.length })}
                      </span>
                      <span className="hidden md:inline">
                        {isScreening
                          ? t('resultsTableVirtualized.scanning', { defaultValue: 'Scanning... {{count}} so far', count: stocks.length })
                          : showOnlySupportLines
                          ? t('resultsTableVirtualized.stocksWithSupport', { defaultValue: '{{count}} stocks w/ support', count: stocks.length })
                          : t('resultsTableVirtualized.stocksFound', { defaultValue: '{{count}} stocks found', count: stocks.length })}
                      </span>
                      <span className="text-text-muted">·</span>
                      <span>{t('resultsTableVirtualized.selectedCount', { defaultValue: '{{count}} selected', count: selectedVisibleCount })}</span>
                      {screeningStatus.last_updated && (
                        <span className="text-text-muted text-[10px] md:text-xs hidden md:inline">
                          <span className="text-text-muted"> · </span>
                          {new Date(
                            screeningStatus.last_updated
                          ).toLocaleDateString(undefined, {
                            month: 'numeric',
                            day: 'numeric',
                            year: '2-digit',
                          })}
                          ,{' '}
                          {new Date(
                            screeningStatus.last_updated
                          ).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Search + toolbar row: compact native-style on mobile */}
                <div className="flex flex-col gap-2 w-full md:w-auto md:flex-row md:items-center md:gap-2 md:flex-wrap md:flex-initial">
                  {/* Search - full width / professional on mobile; compact original on desktop */}
                  <div
                    className="relative flex-1 md:flex-none md:max-w-[140px]"
                    style={{ minWidth: 0 }}
                  >
                    <i
                      className="fas fa-search absolute left-2.5 md:left-2 top-1/2 -translate-y-1/2 pointer-events-none text-sm md:text-[10px]"
                      style={{
                        color: searchTerm.trim()
                          ? 'rgba(255,255,255,0.95)'
                          : 'rgba(255,255,255,0.6)',
                      }}
                    />
                    <input
                      type="text"
                      placeholder={t('resultsTableVirtualized.searchPlaceholder', { defaultValue: 'Search...' })}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={cn(
                        'w-full text-text-primary pl-9 pr-9 border focus:outline-none focus:ring-2 focus:ring-accent-primary/40 transition-colors',
                        'rounded-lg py-2 text-sm min-h-[38px]',
                        'md:rounded-lg md:py-1 md:min-h-[30px] md:text-[11px] md:pl-7 md:pr-7',
                        'max-md:bg-surface-container-highest max-md:border-border-default max-md:placeholder-text-muted',
                        'md:bg-surface-container-highest md:border-border-default md:placeholder-text-muted',
                        'max-md:focus:bg-surface-container-high max-md:focus:border-border-strong',
                        'md:focus:bg-surface-container-highest md:focus:border-border-strong'
                      )}
                      style={
                        searchTerm.trim()
                          ? {
                              backgroundColor: 'var(--surface_container_high)',
                              borderColor: 'var(--border-strong)',
                            }
                          : undefined
                      }
                    />
                    {searchTerm.trim() && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm('')}
                        className="absolute right-2.5 md:right-1.5 top-1/2 -translate-y-1/2 p-1.5 md:p-1 text-text-muted hover:text-text-primary rounded border-0 bg-transparent cursor-pointer touch-manipulation"
                        title={t('resultsTableVirtualized.clearSearch', { defaultValue: 'Clear search' })}
                      >
                        <i className="fas fa-times text-xs" />
                      </button>
                    )}
                  </div>

                  {/* Result filters: combinable Score range + Entry-Status
                      multi-select. Wraps in the same flex row as search. */}
                  <div className="flex w-full min-w-0 flex-wrap items-center gap-1.5 md:w-auto">
                    {/* Score numeric-range filter */}
                    <div className="flex items-center gap-1 rounded-lg bg-surface-container-highest px-2 py-1 min-h-[38px] md:min-h-[30px] md:py-1">
                      <span
                        id="score-filter-label"
                        className="text-[11px] font-medium text-text-secondary"
                      >
                        {t('resultsTableVirtualized.scoreFilterLabel', { defaultValue: 'Score' })}
                      </span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={10}
                        step={1}
                        aria-labelledby="score-filter-label"
                        aria-label={t('resultsTableVirtualized.scoreMinAria', { defaultValue: 'Minimum score' })}
                        placeholder={t('resultsTableVirtualized.scoreMinPlaceholder', { defaultValue: 'min' })}
                        value={scoreMin}
                        onChange={(e) => setScoreMin(e.target.value)}
                        className="w-12 bg-transparent text-text-primary text-[11px] text-center border-0 focus:outline-none focus:ring-1 focus:ring-accent-primary/40 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <span className="text-[11px] text-text-muted">–</span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={10}
                        step={1}
                        aria-label={t('resultsTableVirtualized.scoreMaxAria', { defaultValue: 'Maximum score' })}
                        placeholder={t('resultsTableVirtualized.scoreMaxPlaceholder', { defaultValue: 'max' })}
                        value={scoreMax}
                        onChange={(e) => setScoreMax(e.target.value)}
                        className="w-12 bg-transparent text-text-primary text-[11px] text-center border-0 focus:outline-none focus:ring-1 focus:ring-accent-primary/40 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      {scoreFilterActive && (
                        <button
                          type="button"
                          onClick={() => {
                            setScoreMin('');
                            setScoreMax('');
                          }}
                          className="ml-0.5 p-0.5 text-text-muted hover:text-text-primary rounded border-0 bg-transparent cursor-pointer"
                          title={t('resultsTableVirtualized.scoreFilterClear', { defaultValue: 'Clear score filter' })}
                          aria-label={t('resultsTableVirtualized.scoreFilterClear', { defaultValue: 'Clear score filter' })}
                        >
                          <i className="fas fa-times text-[10px]" />
                        </button>
                      )}
                    </div>

                    {/* Entry-Status multi-select */}
                    <div className="relative" ref={entryStatusMenuRef}>
                      <button
                        type="button"
                        onClick={() => setEntryStatusMenuOpen((v) => !v)}
                        aria-haspopup="true"
                        aria-expanded={entryStatusMenuOpen}
                        className={cn(
                          'flex items-center gap-1.5 rounded-lg border bg-surface-container-highest px-2.5 py-1 min-h-[38px] md:min-h-[36px] md:py-1 text-[11px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-accent-primary/40',
                          entryStatusFilter.length > 0
                            ? 'border-accent-primary text-text-primary'
                            : 'border-border-default text-text-secondary hover:text-text-primary'
                        )}
                        title={t('resultsTableVirtualized.entryStatusFilterTitle', { defaultValue: 'Filter by entry status' })}
                      >
                        <i className="fas fa-filter text-[10px]" />
                        <span>{t('resultsTableVirtualized.entryStatusFilterLabel', { defaultValue: 'Entry Status' })}</span>
                        {entryStatusFilter.length > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-accent-primary text-on-primary text-[10px] font-bold leading-none">
                            {entryStatusFilter.length}
                          </span>
                        )}
                        <i className={cn('fas fa-chevron-down text-[9px] transition-transform', entryStatusMenuOpen && 'rotate-180')} />
                      </button>
                      {entryStatusMenuOpen && (
                        <div
                          role="menu"
                          className="absolute z-[100] mt-1 right-0 md:left-0 w-56 max-h-72 overflow-auto rounded-lg bg-surface-container-high shadow-lg p-1"
                        >
                          <div className="flex items-center justify-between px-2 py-1 border-b border-border-subtle mb-1">
                            <span className="text-[11px] font-semibold text-text-secondary">
                              {t('resultsTableVirtualized.entryStatusFilterHeading', { defaultValue: 'Entry Status' })}
                            </span>
                            {entryStatusFilter.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setEntryStatusFilter([])}
                                className="text-[10px] text-accent-primary hover:underline border-0 bg-transparent cursor-pointer"
                              >
                                {t('resultsTableVirtualized.entryStatusFilterClear', { defaultValue: 'Clear' })}
                              </button>
                            )}
                          </div>
                          {ENTRY_STATUS_FILTER_CODES.map((code) => {
                            const display = resolveEntryStatus(code);
                            const checked = entryStatusFilter.includes(code);
                            const label = isKnownEntryStatus(code)
                              ? t(
                                  `resultsTableVirtualized.${ENTRY_STATUS_I18N_KEY[code]}`,
                                  { defaultValue: display.label }
                                )
                              : t('resultsTableVirtualized.entryStatusUnknownLabel', { defaultValue: 'Unknown' });
                            return (
                              <label
                                key={code}
                                className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-surface-container-highest text-[12px] text-text-primary"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleEntryStatus(code)}
                                  className="accent-accent-primary cursor-pointer"
                                />
                                {display.emoji && (
                                  <span aria-hidden="true">{display.emoji}</span>
                                )}
                                <span>{label}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {anyResultFilterActive && (
                      <button
                        type="button"
                        onClick={clearResultFilters}
                        className="flex items-center gap-1 rounded-lg bg-surface-container px-2 py-1 min-h-[38px] md:min-h-[30px] md:py-1 text-[11px] text-text-secondary hover:text-text-primary hover:bg-surface-container-high transition-colors focus:outline-none focus:ring-2 focus:ring-accent-primary/40"
                        title={t('resultsTableVirtualized.clearAllFiltersTitle', { defaultValue: 'Clear all filters' })}
                      >
                        <i className="fas fa-rotate-left text-[10px]" />
                        <span>{t('resultsTableVirtualized.clearAllFilters', { defaultValue: 'Clear filters' })}</span>
                      </button>
                    )}
                  </div>

                  {/* Mobile controls: compact row - flex + min-w-0 avoids horizontal scroll */}
                  <div className="md:hidden flex w-full min-w-0 items-center gap-1">
                    <select
                      value={sortConfig.column || ''}
                      onChange={(e) => {
                        const column = e.target.value;
                        if (column) {
                          dispatch(
                            setSortConfig({
                              column,
                              direction:
                                sortConfig.column === column &&
                                sortConfig.direction === 'desc'
                                  ? 'asc'
                                  : 'desc',
                            })
                          );
                        }
                      }}
                      className="min-w-0 flex-1 basis-0 bg-surface-container-highest border border-border-subtle text-text-primary text-[11px] rounded-md py-1 px-1.5 min-h-[36px] max-h-[36px] focus:outline-none focus:ring-2 focus:ring-accent-primary/40"
                      title={t('resultsTableVirtualized.sortByTitle', { defaultValue: 'Sort by' })}
                    >
                      <option value="" className="text-gray-800">
                        {t('resultsTableVirtualized.sortDefault', { defaultValue: 'Default' })}
                      </option>
                      <option
                        value="day_change_percent"
                        className="text-gray-800"
                      >
                        {t('resultsTableVirtualized.sortDaily', { defaultValue: '% Daily' })}
                      </option>
                      <option value="ticker" className="text-gray-800">
                        {t('resultsTableVirtualized.sortAZ', { defaultValue: 'A-Z' })}
                      </option>
                      <option value="score" className="text-gray-800">
                        {t('resultsTableVirtualized.sortScore', { defaultValue: 'Score' })}
                      </option>
                      <option
                        value="ma_150_distance_pct"
                        className="text-gray-800"
                      >
                        {t('resultsTableVirtualized.sort150Ma', { defaultValue: '% 150 MA' })}
                      </option>
                    </select>
                    <div className="inline-flex shrink-0 items-center bg-white/10 border border-white/20 rounded-md overflow-hidden min-h-[36px] max-h-[36px]">
                      <button
                        type="button"
                        onClick={() => {
                          if (sortConfig.column) {
                            dispatch(
                              setSortConfig({
                                column: sortConfig.column,
                                direction: 'asc',
                              })
                            );
                          }
                        }}
                        disabled={!sortConfig.column}
                        className={cn(
                          'w-9 min-w-[36px] min-h-[36px] px-0 text-[10px] text-text-primary transition-colors flex items-center justify-center',
                          sortConfig.direction === 'asc'
                            ? 'bg-surface-container-highest'
                            : 'hover:bg-surface-container',
                          !sortConfig.column && 'opacity-50 cursor-not-allowed'
                        )}
                        title={t('resultsTableVirtualized.ascending', { defaultValue: 'Ascending' })}
                      >
                        <i className="fas fa-sort-up text-[11px]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (sortConfig.column) {
                            dispatch(
                              setSortConfig({
                                column: sortConfig.column,
                                direction: 'desc',
                              })
                            );
                          }
                        }}
                        disabled={!sortConfig.column}
                        className={cn(
                          'w-9 min-w-[36px] min-h-[36px] px-0 text-[10px] text-text-primary border-l border-border-subtle transition-colors flex items-center justify-center',
                          sortConfig.direction === 'desc'
                            ? 'bg-surface-container-highest'
                            : 'hover:bg-surface-container',
                          !sortConfig.column && 'opacity-50 cursor-not-allowed'
                        )}
                        title={t('resultsTableVirtualized.descending', { defaultValue: 'Descending' })}
                      >
                        <i className="fas fa-sort-down text-[11px]" />
                      </button>
                    </div>
                    {isMobileView && (
                      <div className="relative shrink-0" ref={toolbarMenuRef}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setToolbarMenuOpen((v) => !v);
                          }}
                          className="w-9 min-w-[36px] min-h-[36px] max-h-[36px] rounded-md border bg-surface-container text-text-primary border-border-default text-xs font-medium hover:bg-surface-container-high active:bg-surface-container-highest focus:outline-none focus:ring-2 focus:ring-accent-primary/40 transition-colors touch-manipulation active:scale-[0.97] flex items-center justify-center"
                          aria-label={t('resultsTableVirtualized.moreActions', { defaultValue: 'More actions' })}
                          title={t('resultsTableVirtualized.moreActions', { defaultValue: 'More actions' })}
                        >
                          <i className="fas fa-ellipsis-v text-text-primary text-sm" />
                        </button>
                        {toolbarMenuOpen && isMobileView && (
                          <div
                            className="fixed inset-0 z-[120] md:hidden"
                            role="dialog"
                            aria-modal="true"
                            aria-label={t('resultsTableVirtualized.moreActions', { defaultValue: 'More actions' })}
                          >
                            <button
                              type="button"
                              className="absolute inset-0 bg-[color:var(--overlay)] backdrop-blur-[1px] tv-mobile-sheet-backdrop"
                              onClick={() => setToolbarMenuOpen(false)}
                              aria-label={t('resultsTableVirtualized.closeActionsMenu', { defaultValue: 'Close actions menu' })}
                              style={{
                                opacity: Math.max(0.2, 1 - sheetDragY / 260),
                              }}
                            />
                            <div
                              className={cn(
                                'absolute bottom-0 left-0 right-0 rounded-t-2xl bg-surface-container shadow-2xl px-3 pt-2 pb-4 tv-mobile-sheet',
                                sheetDragY > 0 && 'transition-none'
                              )}
                              onTouchStart={handleSheetTouchStart}
                              onTouchMove={handleSheetTouchMove}
                              onTouchEnd={handleSheetTouchEnd}
                              onTouchCancel={handleSheetTouchEnd}
                              style={{
                                transform: `translateY(${sheetDragY}px)`,
                              }}
                            >
                              <div
                                className="w-10 h-1 mx-auto rounded-full bg-white/25 mb-2 cursor-grab active:cursor-grabbing touch-manipulation"
                                onMouseDown={(e) => startHandleDrag(e.clientY)}
                                onMouseUp={endHandleDrag}
                                onMouseLeave={endHandleDrag}
                                onTouchStart={(e) =>
                                  startHandleDrag(e.touches[0]?.clientY ?? 0)
                                }
                                onTouchEnd={endHandleDrag}
                                onTouchCancel={endHandleDrag}
                              />
                              <div className="text-xs text-text-secondary px-1 mb-1">
                                {t('resultsTableVirtualized.quickActions', { defaultValue: 'Quick actions' })}
                              </div>
                              <div className="space-y-1.5">
                                {/* Screen / Filters / Finviz - relocated from ActionButtons mobile bar */}
                                {isScreening ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setToolbarMenuOpen(false);
                                      dispatch(stopScreeningThunk());
                                    }}
                                    className="w-full text-left px-3 py-2.5 text-sm text-white bg-accent-bearish/80 hover:bg-accent-bearish rounded-xl flex items-center gap-2 active:scale-[0.99] transition-transform"
                                  >
                                    <i className="fas fa-stop" /> {t('resultsTableVirtualized.stopScreening', { defaultValue: 'Stop Screening' })}
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setToolbarMenuOpen(false);
                                      dispatch(screenStocksThunk());
                                    }}
                                    className="w-full text-left px-3 py-2.5 text-sm text-black bg-accent-bullish/90 hover:bg-accent-bullish rounded-xl flex items-center gap-2 active:scale-[0.99] transition-transform font-medium"
                                  >
                                    <i className="fas fa-search" /> {t('resultsTableVirtualized.screenStocks', { defaultValue: 'Screen Stocks' })}
                                  </button>
                                )}

                                {isAdmin && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setToolbarMenuOpen(false);
                                      dispatch(openLegacyFiltersModal());
                                    }}
                                    className="w-full text-left px-3 py-2.5 text-sm text-text-primary bg-surface-container hover:bg-surface-container-high rounded-xl flex items-center gap-2 active:scale-[0.99] transition-transform"
                                  >
                                    <i className="fas fa-filter" /> {t('resultsTableVirtualized.legacyFilters', { defaultValue: 'Legacy Filters' })}
                                  </button>
                                )}
                                <div className="h-px bg-white/10 my-1" />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowOnlySupportLines(
                                      !showOnlySupportLines
                                    );
                                    setToolbarMenuOpen(false);
                                  }}
                                  disabled={!anySupportLineFilterable}
                                  title={
                                    !anySupportLineFilterable
                                      ? t('resultsTableVirtualized.supportFilterNoData', { defaultValue: 'No support-line data in these results' })
                                      : undefined
                                  }
                                  className="w-full text-left px-3 py-2.5 text-sm text-text-primary bg-surface-container hover:bg-surface-container-high rounded-xl flex items-center gap-2 active:scale-[0.99] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <i
                                    className={`fas ${showOnlySupportLines ? 'fa-filter' : 'fa-chart-line'}`}
                                  />
                                  {showOnlySupportLines
                                    ? t('resultsTableVirtualized.supportOnly', { defaultValue: 'Support Only' })
                                    : t('resultsTableVirtualized.supportFilter', { defaultValue: 'Support Filter' })}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleSelectAll();
                                    setToolbarMenuOpen(false);
                                  }}
                                  className="w-full text-left px-3 py-2.5 text-sm text-text-primary bg-surface-container hover:bg-surface-container-high rounded-xl flex items-center gap-2 active:scale-[0.99] transition-transform"
                                >
                                  <i className="fas fa-check-square" /> {t('resultsTableVirtualized.selectAll', { defaultValue: 'Select All' })}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleUnselectAll();
                                    setToolbarMenuOpen(false);
                                  }}
                                  disabled={selectedVisibleCount === 0}
                                  className="w-full text-left px-3 py-2.5 text-sm text-text-primary bg-surface-container/70 hover:bg-surface-container rounded-xl flex items-center gap-2 disabled:opacity-50 active:scale-[0.99] transition-transform"
                                >
                                  <i className="fas fa-square" /> {t('resultsTableVirtualized.none', { defaultValue: 'None' })}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    copySelectedToClipboard();
                                    setToolbarMenuOpen(false);
                                  }}
                                  disabled={selectedVisibleCount === 0}
                                  className="w-full text-left px-3 py-2.5 text-sm text-text-primary bg-surface-container/70 hover:bg-surface-container rounded-xl flex items-center gap-2 disabled:opacity-50 active:scale-[0.99] transition-transform"
                                >
                                  <i className="fas fa-copy" /> {t('resultsTableVirtualized.exportSelectedCount', { defaultValue: 'Export Selected ({{count}})', count: selectedVisibleCount })}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    exportSelectedTickers();
                                    setToolbarMenuOpen(false);
                                  }}
                                  disabled={
                                    selectedVisibleCount === 0 || isExporting
                                  }
                                  className="w-full text-left px-3 py-2.5 text-sm text-text-primary bg-surface-container/70 hover:bg-surface-container rounded-xl flex items-center gap-2 disabled:opacity-50 active:scale-[0.99] transition-transform"
                                >
                                  {isExporting ? (
                                    <>
                                      <LoadingSpinner size="sm" /> {t('resultsTableVirtualized.exporting', { defaultValue: 'Export...' })}
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-download" /> {t('resultsTableVirtualized.exportCount', { defaultValue: 'Export ({{count}})', count: selectedVisibleCount })}
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                        {toolbarMenuOpen && !isMobileView && (
                          <div
                            className="absolute right-0 top-full mt-1 py-2 bg-surface-container rounded-lg shadow-xl z-[100] min-w-[220px]"
                            role="menu"
                          >
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setShowOnlySupportLines(!showOnlySupportLines);
                                setToolbarMenuOpen(false);
                              }}
                              disabled={!anySupportLineFilterable}
                              title={
                                !anySupportLineFilterable
                                  ? t('resultsTableVirtualized.supportFilterNoData', { defaultValue: 'No support-line data in these results' })
                                  : undefined
                              }
                              className="w-full text-left px-4 py-3 text-sm text-text-primary hover:bg-surface-container-high flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <i
                                className={`fas ${showOnlySupportLines ? 'fa-filter' : 'fa-chart-line'}`}
                              />
                              {showOnlySupportLines
                                ? t('resultsTableVirtualized.supportOnly', { defaultValue: 'Support Only' })
                                : t('resultsTableVirtualized.supportFilter', { defaultValue: 'Support Filter' })}
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                handleSelectAll();
                                setToolbarMenuOpen(false);
                              }}
                              className="w-full text-left px-4 py-3 text-sm text-text-primary hover:bg-surface-container-high flex items-center gap-2"
                            >
                              <i className="fas fa-check-square" /> {t('resultsTableVirtualized.selectAll', { defaultValue: 'Select All' })}
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                handleUnselectAll();
                                setToolbarMenuOpen(false);
                              }}
                              disabled={selectedVisibleCount === 0}
                              className="w-full text-left px-4 py-3 text-sm text-text-primary hover:bg-surface-container-high flex items-center gap-2 disabled:opacity-50"
                            >
                              <i className="fas fa-square" /> {t('resultsTableVirtualized.none', { defaultValue: 'None' })}
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                copySelectedToClipboard();
                                setToolbarMenuOpen(false);
                              }}
                              disabled={selectedVisibleCount === 0}
                              className="w-full text-left px-4 py-3 text-sm text-text-primary hover:bg-surface-container-high flex items-center gap-2 disabled:opacity-50"
                            >
                              <i className="fas fa-copy" /> {t('resultsTableVirtualized.exportSelectedCount', { defaultValue: 'Export Selected ({{count}})', count: selectedVisibleCount })}
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                exportSelectedTickers();
                                setToolbarMenuOpen(false);
                              }}
                              disabled={
                                selectedVisibleCount === 0 || isExporting
                              }
                              className="w-full text-left px-4 py-3 text-sm text-text-primary hover:bg-surface-container-high flex items-center gap-2 disabled:opacity-50"
                            >
                              {isExporting ? (
                                <>
                                  <LoadingSpinner size="sm" /> {t('resultsTableVirtualized.exporting', { defaultValue: 'Export...' })}
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-download" /> {t('resultsTableVirtualized.exportCount', { defaultValue: 'Export ({{count}})', count: selectedVisibleCount })}
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {!isMobileView && (
                    <button
                      onClick={() =>
                        setShowOnlySupportLines(!showOnlySupportLines)
                      }
                      disabled={!anySupportLineFilterable}
                      className="tv-btn tv-btn-sm tv-flex tv-items-center tv-gap-1 min-h-[36px]"
                      style={{
                        backgroundColor: showOnlySupportLines
                          ? 'var(--accent-info)'
                          : 'var(--surface_container_highest)',
                        color: showOnlySupportLines ? 'var(--on_primary)' : 'var(--text-primary)',
                        border: showOnlySupportLines
                          ? '2px solid var(--accent-info)'
                          : '1px solid var(--border-default)',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        fontSize: '0.6875rem',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        opacity: anySupportLineFilterable ? undefined : 0.5,
                        cursor: anySupportLineFilterable ? undefined : 'not-allowed',
                      }}
                      title={
                        !anySupportLineFilterable
                          ? t('resultsTableVirtualized.supportFilterNoData', { defaultValue: 'No support-line data in these results' })
                          : showOnlySupportLines
                          ? t('resultsTableVirtualized.showingSupportOnly', { defaultValue: 'Showing only stocks with support lines - Click to show all' })
                          : t('resultsTableVirtualized.showSupportOnly', { defaultValue: 'Show only stocks with support lines' })
                      }
                    >
                      <i
                        className={`fas ${
                          showOnlySupportLines ? 'fa-filter' : 'fa-chart-line'
                        }`}
                      ></i>
                      {showOnlySupportLines
                        ? t('resultsTableVirtualized.supportOnly', { defaultValue: 'Support Only' })
                        : t('resultsTableVirtualized.supportFilter', { defaultValue: 'Support Filter' })}
                    </button>
                  )}

                  {/* Cup&Handle: reveal borderline Grade-B candidates (hidden by
                      default). Only shown when there are B rows to reveal. */}
                  {!isMobileView && borderlineCount > 0 && (
                    <button
                      onClick={() => setShowBorderline((v) => !v)}
                      className="tv-btn tv-btn-sm tv-flex tv-items-center tv-gap-1 min-h-[36px]"
                      style={{
                        backgroundColor: showBorderline
                          ? '#d97706'
                          : 'var(--surface_container_highest)',
                        color: showBorderline ? 'white' : 'var(--text-primary)',
                        border: showBorderline
                          ? '2px solid #d97706'
                          : '1px solid var(--border-default)',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        fontSize: '0.6875rem',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                      }}
                      title={
                        showBorderline
                          ? t('resultsTableVirtualized.hidingBorderline', { defaultValue: 'Hiding borderline Grade-B candidates' })
                          : t('resultsTableVirtualized.showBorderlineTitle', { defaultValue: 'Show {{count}} borderline (Grade B) candidate', count: borderlineCount })
                      }
                    >
                      <i className={`fas ${showBorderline ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                      {showBorderline
                        ? t('resultsTableVirtualized.hideBorderline', { defaultValue: 'Hide borderline (B)' })
                        : t('resultsTableVirtualized.showBorderline', { defaultValue: 'Show borderline (B) · {{count}}', count: borderlineCount })}
                    </button>
                  )}

                  {/* Select/Unselect All Buttons */}
                  {!isMobileView && (
                    <div className="tv-flex tv-gap-1">
                      <label
                        className="tv-btn tv-btn-sm tv-flex tv-items-center tv-gap-1 min-h-[36px]"
                        style={{
                          backgroundColor: 'var(--surface_container_highest)',
                          color: 'var(--text-primary)',
                          border: '1px solid var(--border-default)',
                          padding: '5px 8px',
                          borderRadius: '4px',
                          fontSize: '0.6875rem',
                          fontWeight: '500',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                        }}
                        title={t('resultsTableVirtualized.selectAllStocks', { defaultValue: 'Select all {{count}} stocks', count: stocks.length })}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('resultsTableVirtualized.selectAllStocksAria', { defaultValue: 'Select all stocks' })}
                          title={t('resultsTableVirtualized.selectAllStocks', { defaultValue: 'Select all {{count}} stocks', count: stocks.length })}
                          checked={
                            stocks.length > 0 &&
                            selectedVisibleCount === stocks.length
                          }
                          onChange={(e) => {
                            if (e.target.checked) handleSelectAll();
                            else handleUnselectAll();
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <i
                          className="fas fa-check-square"
                          aria-hidden="true"
                        ></i>
                        {t('table.tbl.all', { defaultValue: 'All' })}
                      </label>
                      <button
                        onClick={handleUnselectAll}
                        disabled={selectedVisibleCount === 0}
                        className="tv-btn tv-btn-sm tv-flex tv-items-center tv-gap-1 min-h-[36px]"
                        style={{
                          backgroundColor:
                            selectedVisibleCount === 0
                              ? 'var(--surface_container)'
                              : 'var(--surface_container_highest)',
                          color:
                            selectedVisibleCount === 0
                              ? 'var(--text-muted)'
                              : 'var(--text-primary)',
                          border: '1px solid var(--border-default)',
                          padding: '5px 8px',
                          borderRadius: '4px',
                          fontSize: '0.6875rem',
                          fontWeight: '500',
                          whiteSpace: 'nowrap',
                        }}
                        title={t('table.unselectAll', { defaultValue: 'Unselect all stocks' })}
                      >
                        <i className="fas fa-square"></i>
                        {t('table.tbl.none', { defaultValue: 'None' })}
                      </button>
                    </div>
                  )}

                  {/* Export All (clipboard) */}
                  {!isMobileView && stocks.length > 0 && (
                    <button
                      onClick={exportAllTickersToClipboard}
                      data-flash={exportFeedback === 'all' ? 'on' : 'off'}
                      className="tv-btn tv-btn-sm tv-flex tv-items-center tv-gap-1 min-h-[36px] transition-transform duration-150 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/60"
                      style={{
                        backgroundColor:
                          exportFeedback === 'all'
                            ? 'var(--accent-bullish)'
                            : 'var(--accent-info)',
                        color: 'var(--on_primary)',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        fontSize: '0.6875rem',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                        transition:
                          'background-color 180ms ease, color 180ms ease, transform 150ms ease',
                      }}
                      title={t('table.tbl.exportAllTitle', { defaultValue: 'Export all {{count}} tickers to clipboard', count: stocks.length })}
                    >
                      {exportFeedback === 'all' ? (
                        <>
                          <i className="fas fa-check" />
                          {t('table.tbl.copied', { defaultValue: 'Copied!' })}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-copy" />
                          {t('table.tbl.exportAllCount', { defaultValue: 'Export All ({{count}})', count: stocks.length })}
                        </>
                      )}
                    </button>
                  )}

                  {/* Export Selected (clipboard) */}
                  {!isMobileView && (
                    <button
                      onClick={copySelectedToClipboard}
                      disabled={selectedVisibleCount === 0}
                      data-flash={exportFeedback === 'selected' ? 'on' : 'off'}
                      className="tv-btn tv-btn-sm tv-flex tv-items-center tv-gap-1 min-h-[36px] transition-transform duration-150 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/60 disabled:active:scale-100"
                      style={{
                        backgroundColor:
                          selectedVisibleCount === 0
                            ? 'var(--surface_container)'
                            : exportFeedback === 'selected'
                              ? 'var(--accent-bullish)'
                              : 'var(--accent-info)',
                        color:
                          selectedVisibleCount === 0
                            ? 'var(--text-muted)'
                            : 'var(--on_primary)',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        fontSize: '0.6875rem',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                        transition:
                          'background-color 180ms ease, color 180ms ease, transform 150ms ease',
                      }}
                      title={t('table.tbl.exportSelectedClipboardTitle', { defaultValue: 'Export Selected {{count}} tickers to clipboard', count: selectedVisibleCount })}
                    >
                      {exportFeedback === 'selected' && selectedVisibleCount > 0 ? (
                        <>
                          <i className="fas fa-check" />
                          {t('table.tbl.copied', { defaultValue: 'Copied!' })}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-copy" />
                          {t('resultsTableVirtualized.exportSelectedCount', { defaultValue: 'Export Selected ({{count}})', count: selectedVisibleCount })}
                        </>
                      )}
                    </button>
                  )}

                  {/* Export CSV Button */}
                  {!isMobileView && (
                    <button
                      onClick={exportSelectedTickers}
                      disabled={selectedVisibleCount === 0 || isExporting}
                      data-flash={exportFeedback === 'csv' ? 'on' : 'off'}
                      className="tv-btn tv-btn-success tv-btn-sm tv-flex tv-items-center tv-gap-1 min-h-[36px] transition-transform duration-150 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/60 disabled:active:scale-100"
                      style={{
                        backgroundColor:
                          selectedVisibleCount === 0
                            ? 'var(--surface_container)'
                            : exportFeedback === 'csv'
                              ? 'var(--accent-primary)'
                              : 'var(--accent-bullish)',
                        color:
                          selectedVisibleCount === 0
                            ? 'var(--text-muted)'
                            : exportFeedback === 'csv'
                              ? 'var(--on_primary)'
                              : '#ffffff',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        fontSize: '0.6875rem',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                        transition:
                          'background-color 180ms ease, color 180ms ease, transform 150ms ease',
                      }}
                      title={t('table.tbl.exportCsvTitle', { defaultValue: 'Export {{count}} selected tickers to CSV', count: selectedVisibleCount })}
                    >
                      {isExporting ? (
                        <>
                          <LoadingSpinner size="sm" />
                          {t('resultsTableVirtualized.exporting', { defaultValue: 'Export...' })}
                        </>
                      ) : exportFeedback === 'csv' && selectedVisibleCount > 0 ? (
                        <>
                          <i className="fas fa-check" />
                          {t('table.tbl.exported', { defaultValue: 'Exported!' })}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-download"></i>
                          {t('resultsTableVirtualized.exportCount', { defaultValue: 'Export ({{count}})', count: selectedVisibleCount })}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Virtualized Table or No Results Message */}
            {stocks.length === 0 ? (
              <div
                data-testid="mobile-results-scroll"
                className="tv-flex tv-flex-col tv-items-center tv-justify-center flex-1 min-h-0 overflow-y-auto md:overflow-visible -mx-3 md:mx-0"
                style={{
                  padding: '80px 20px',
                  textAlign: 'center',
                }}
              >
                <i
                  className="fas fa-search"
                  style={{
                    fontSize: '3rem',
                    color: 'var(--tv-text-secondary)',
                    marginBottom: '20px',
                    opacity: 0.5,
                  }}
                ></i>
                <h5
                  style={{
                    color: 'var(--tv-text-primary)',
                    marginBottom: '12px',
                  }}
                >
                  {t('table.tbl.noStocksMatchSearch', { defaultValue: 'No stocks match your search' })}
                </h5>
                <p
                  style={{
                    color: 'var(--tv-text-secondary)',
                    maxWidth: '400px',
                  }}
                >
                  {searchTerm.trim() ? (
                    <Trans
                      i18nKey="table.tbl.noResultsForSearch"
                      values={{ term: searchTerm }}
                      defaults='No results found for "<1>{{term}}</1>". Try a different search term or clear the search to see all results.'
                      components={[<span />, <strong />]}
                    />
                  ) : showOnlySupportLines ? (
                    t('table.tbl.noStocksWithSupport', { defaultValue: 'No stocks with support lines found. Try disabling the support lines filter to see more results.' })
                  ) : (
                    t('table.tbl.noStocksMatchFilters', { defaultValue: 'No stocks match the current filters. Try adjusting your filter criteria.' })
                  )}
                </p>
                {searchTerm.trim() && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="tv-btn tv-btn-primary tv-mt-3"
                    style={{
                      marginTop: '20px',
                      padding: '8px 20px',
                      backgroundColor: 'var(--accent-primary)',
                      color: 'var(--on_primary)',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                    }}
                  >
                    <i className="fas fa-times tv-mr-2"></i>
                    {t('table.clearSearch', { defaultValue: 'Clear search' })}
                  </button>
                )}
              </div>
            ) : (
              <div
                ref={tableContainerRef}
                className="flex-1 min-h-0 flex flex-col md:pt-0"
              >
                {/* Desktop: Virtualized Table View */}
                <div className="hidden md:block flex-1 min-h-0">
                  <VirtualizedTable
                    ref={tableRef}
                    data={stocks}
                    columns={columns}
                    height={tableHeight}
                    rowHeight={80}
                    onRowClick={handleRowClick}
                    onSort={handleSort}
                    sortConfig={{
                      column: sortConfig.column as keyof StockData | null,
                      direction: sortConfig.direction,
                    }}
                    itemKey={itemKey}
                    overscanCount={10}
                    className="results-table-virtualized"
                    selectedRows={selectedStocks}
                    rowKeyField="ticker"
                    currentRowIndex={currentRowIndex}
                  />
                </div>

                {/* Mobile: StockCard List View (disabled in Jest) */}
                {!isTestEnv && (
                  <div
                    data-testid="mobile-results-scroll"
                    className="md:hidden min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain -mx-3 pb-20"
                  >
                    {stocks.map((stock: StockData, index: number) => (
                      <StockCard
                        key={stock.ticker}
                        stock={stock}
                        onPress={(ticker) => {
                          setSelectedStocks(new Set([ticker]));
                          setCurrentRowIndex(index);
                          dispatch(openPanel({ ticker }));
                        }}
                        isSelected={selectedStocks.has(stock.ticker)}
                      />
                    ))}
                    {stocks.length === 0 && (
                      <div className="text-center py-8 text-text-muted">
                        {t('table.tbl.noStocksToDisplay', { defaultValue: 'No stocks to display' })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
});

ResultsTableVirtualized.displayName = 'ResultsTableVirtualized';

export default ResultsTableVirtualized;
